import { useState, useEffect, useRef, Fragment } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry'
import api from '../services/api'
import { formatMoney } from './Apolices'
import { useNavegacaoLista } from '../hooks/useNavegacaoLista'
import { escapeHtml } from '../utils/html'
import IconExcluir from '../components/icons/IconExcluir'
import IconEditar from '../components/icons/IconEditar'
import { avisar, confirmar } from '../components/Avisos'

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker

// Canvas nao entende var(--primary) — precisa do hex resolvido de verdade (a cor de destaque
// customizavel em Cadastro > Corretora ja foi aplicada nessa CSS var no carregamento do app).
const corPrimariaResolvida = () => getComputedStyle(document.documentElement).getPropertyValue('--primary').trim() || '#DD3B00'

// Opcoes pre-definidas de Coberturas — mesma lista da ferramenta desktop cotacao-standalone
// (main.py, OPCOES_COBERTURA), pra deixar a tela de edicao da Cotação PDF do CRM igual a do
// programa do computador. As chaves dos campos de "Dados Gerais" (ver card correspondente no
// render) tem que bater com o que o backend (mesclar-pdf/cotacao_emseg.py, _parse_compacto) usa.
const OPCOES_COBERTURA = {
  'cobertura': [
    'Compreensiva (Seguro Total)', 'Incêndio, Roubo/Furto', 'Roubo/Furto',
    'Roubo/Furto + PT por Colisão', 'Só Terceiros',
  ],
  'carro reserva': [
    '7 dias (básico)', '15 dias (básico)', '30 dias (básico)', '35 dias (básico)',
    '7 dias p/ perda parcial ou Desc na Franquia',
  ],
  'assistência': [
    'Guincho km ilimitado', 'Guincho 100 km', 'Guincho 200 km', 'Guincho 300 km',
    'Guincho 400 km', 'Guincho 500 km', 'Guincho 600 km', 'Guincho 800 km', 'Guincho 1.000 km',
    'Guincho km ilimitado Sinistros / 500km Panes', 'Guincho km ilimitado Sinistros / 1.000km Panes',
    'Guincho 300km Sinistros / 200km Panes', 'Guincho 500km Sinistros / 400km Panes',
  ],
  'vidros': [
    'Vidros, Retrovisores, Faróis e Lanternas', 'Vidros Parabrisas e laterais',
    'Vidros, Retrovisores, Faróis, Lanternas e Teto Solar',
  ],
  'franquia': ['Obrigatória', 'Reduzida', 'Super Reduzida', 'Intermediária'],
}

// Mesma logica de main.py (_get_opcoes_para_label): acha as opcoes pre-definidas pelo trecho do
// nome da cobertura (ex.: rotulo "Assistência 24h" bate com a chave "assistência").
// Larguras compartilhadas entre as tabelas de Coberturas e Parcelamentos, pra as colunas de
// cada seguradora ficarem alinhadas entre os dois cards (pedido do usuário em 12/09).
const LARGURA_COL_LABEL = 160
const LARGURA_COL_SEG = 260

const opcoesParaLabel = label => {
  const l = String(label || '').toLowerCase()
  for (const [chave, opcoes] of Object.entries(OPCOES_COBERTURA)) {
    if (l.includes(chave)) return opcoes
  }
  return null
}

// Campo de texto livre + botão "▼" que mostra TODAS as opções pré-definidas (sem filtrar pelo
// que já está digitado) — equivalente ao botão "▼" do cotacao-standalone (main.py,
// _mostrar_opcoes). Usar <input list="..."> (datalist) aqui faria o navegador filtrar as
// sugestões pelo texto já preenchido automaticamente, escondendo a lista quando o valor não bate
// com nenhuma opção — exatamente o problema relatado (campo já preenchido não abria a lista).
function InputComOpcoes({ value, onChange, opcoes }) {
  const [aberto, setAberto] = useState(false)
  const [pos, setPos] = useState(null)
  const ref = useRef(null)
  useEffect(() => {
    if (!aberto) return
    const fechar = e => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest('[data-opcoes-popup]')) setAberto(false) }
    document.addEventListener('mousedown', fechar)
    window.addEventListener('scroll', fechar, true)
    return () => { document.removeEventListener('mousedown', fechar); window.removeEventListener('scroll', fechar, true) }
  }, [aberto])
  const abrir = () => {
    const r = ref.current.getBoundingClientRect()
    setPos({ top: r.bottom + 2, left: r.left, width: r.width })
    setAberto(a => !a)
  }
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        className="form-input"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        onClick={abrir}
        style={{ fontSize: 12, padding: '6px 26px 6px 8px', cursor: 'pointer' }}
      />
      <span
        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: 9, color: '#888' }}
      >▼</span>
      {aberto && pos && createPortal(
        // Renderizado fora do card/tabela (via portal, position:fixed) — dentro deles, o
        // overflow:auto da tabela cortava a lista (relatado pelo usuário em 12/09).
        <div data-opcoes-popup style={{ position: 'fixed', top: pos.top, left: pos.left, minWidth: Math.max(pos.width, 220), zIndex: 2000, background: '#fff', border: '1px solid #CCC', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 220, overflowY: 'auto' }}>
          {opcoes.map(o => (
            <div
              key={o}
              onClick={() => { onChange(o); setAberto(false) }}
              style={{ padding: '6px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #F0F0F0', whiteSpace: 'nowrap' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#F5F5F5' }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff' }}
            >{o}</div>
          ))}
        </div>,
        document.body
      )}
    </div>
  )
}

// ── Extrai itens do PDF com posição x,y ─────────────────────────────────────
async function extrairItemsPosicao(file) {
  const buffer = await file.arrayBuffer()
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise
  const items = []
  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p)
    const content = await page.getTextContent()
    for (const item of content.items) {
      if (item.str.trim()) items.push({ str: item.str.trim(), x: item.transform[4], y: item.transform[5] })
    }
  }
  return items
}

// ── Agrupa itens em linhas pela posição Y (tolerância 4px) ──────────────────
function agruparLinhas(items, tol = 4) {
  const sorted = [...items].sort((a, b) => b.y - a.y || a.x - b.x)
  const rows = []
  for (const item of sorted) {
    const row = rows.find(r => Math.abs(r.y - item.y) <= tol)
    if (row) { row.cells.push(item); row.cells.sort((a, b) => a.x - b.x) }
    else rows.push({ y: item.y, cells: [item] })
  }
  return rows
}

// ── Parsa seção de tabela com N colunas usando ancoragem pelo label (col 0) ──
// Resolve o problema de células multi-linha e células vazias (ícones ✕) que
// deslocam valores para a linha errada quando se usa agrupamento por y.
function parsearTabelaNCol(rows, startIdx, endIdx, splits) {
  const nCols = splits.length + 1
  const getCol = x => { for (let i = 0; i < splits.length; i++) { if (x <= splits[i]) return i } return splits.length }

  // Coleta todos os itens da fatia de uma vez
  const allItems = rows.slice(startIdx, endIdx).flatMap(r => r.cells)

  // Labels: itens na coluna 0, ordenados de cima para baixo (y decrescente em PDF)
  const labels = allItems.filter(c => getCol(c.x) === 0).sort((a, b) => b.y - a.y)
  if (!labels.length) return []

  const result = []
  for (let li = 0; li < labels.length; li++) {
    const labelY     = labels[li].y
    const prevLabelY = li > 0              ? labels[li - 1].y : Infinity
    const nextLabelY = li + 1 < labels.length ? labels[li + 1].y : -Infinity
    // Fronteiras: ponto médio entre este label e o anterior/próximo
    // Resolve células multi-linha onde o texto fica acima do label centralizado
    const topBound    = (labelY + prevLabelY) / 2
    const bottomBound = (labelY + nextLabelY) / 2
    const rowItems = allItems.filter(c => c.y <= topBound && c.y > bottomBound)
    const byCol = Array.from({ length: nCols }, () => [])
    rowItems.sort((a, b) => b.y - a.y || a.x - b.x).forEach(c => byCol[getCol(c.x)].push(c.str))
    result.push(byCol.map(col => col.join(' ')))
  }
  return result.filter(r => r.some(c => c))
}

// ── Parsa todos os dados do Orça Compacto ───────────────────────────────────
function parsearOrcaCompacto(items) {
  const rows = agruparLinhas(items)
  const findIdx = kw => rows.findIndex(r => r.cells.some(c => c.str.includes(kw)))

  const iDadosCot  = findIdx('DADOS DA COTAÇÃO')
  const iPerfil    = findIdx('PERFIL DO CONDUTOR')
  const iVeiculo   = findIdx('DADOS DO VEÍCULO')
  const iCoberturas = findIdx('COBERTURAS')
  const iFranquia  = findIdx('FRANQUIA')
  const iValor     = findIdx('VALOR DO SEGURO')

  const secaoTexto = (s, e) => rows.slice(s + 1, e).flatMap(r => r.cells.map(c => c.str)).join(' ')
  const get = (txt, re) => { const m = txt.match(re); return m ? m[1].trim() : '' }

  const tCot = iDadosCot !== -1 ? secaoTexto(iDadosCot, iPerfil !== -1 ? iPerfil : iVeiculo) : ''
  const tPerfil = iPerfil !== -1 ? secaoTexto(iPerfil, iVeiculo !== -1 ? iVeiculo : iCoberturas) : ''
  const tVeic  = iVeiculo !== -1 ? secaoTexto(iVeiculo, iCoberturas !== -1 ? iCoberturas : rows.length) : ''

  const dadosCotacao = {
    'Segurado':    get(tCot, /Segurado:\s*(.+?)(?=CPF\/CNPJ:|$)/),
    'CPF/CNPJ':    get(tCot, /CPF\/CNPJ:\s*([\d.\-\/]+)/),
    'CEP':         get(tCot, /CEP:\s*([\d\-]+)/),
    'Tipo Seguro': get(tCot, /Tipo Seguro:\s*([\wçãõáéíóúâêîôûà]+)/i),
    'Vigência':    get(tCot, /Vigência:\s*([\d\/]+ a [\d\/]+)/),
    'Bônus':       get(tCot, /Bônus:\s*(\d+)/),
  }

  const perfilCondutor = {
    'Principal Condutor':          get(tPerfil, /Principal Condutor:\s*(.+?)(?=CPF:|$)/),
    'CPF':                         get(tPerfil, /CPF:\s*([\d.\-\/]+)/),
    'Nascimento':                  get(tPerfil, /Nascimento:\s*(\d{2}\/\d{2}\/\d{4})/),
    'Sexo':                        get(tPerfil, /Sexo:\s*(Masculino|Feminino)/i),
    'Estado Civil':                get(tPerfil, /Estado Civil:\s*(\w+)/),
    'Relação Segurado':            get(tPerfil, /Relação Segurado:\s*(.+?)(?=Garagem|KM|$)/),
    'Garagem na Residência':       get(tPerfil, /Garagem na Residência:\s*(.+?)(?=Garagem no Trabalho|$)/),
    'Garagem no Trabalho':         get(tPerfil, /Garagem no Trabalho:\s*(Sim|Não)/i),
    'Garagem no Local de Estudo':  get(tPerfil, /Garagem no Local de Estudo:\s*(.+?)(?=Tipo de Uso|$)/),
    'Tipo de Uso':                 get(tPerfil, /Tipo de Uso:\s*(\w+)/),
    'Reside 18-25 Anos':           get(tPerfil, /Reside com Pessoas de 18 a 25 Anos:\s*(Sim|Não)/i),
    'Faixa Etária Residentes':     get(tPerfil, /Faixa Etária dos Residentes:\s*(.+?)(?=$|\s{2})/),
    'KM Mensal':                   get(tPerfil, /KM Mensal:\s*(\d+)/),
  }

  const dadosVeiculo = {
    'Veículo':          get(tVeic, /Veículo:\s*(.+?)(?=Fabricação\/Modelo:|$)/),
    'Fabricação/Modelo': get(tVeic, /Fabricação\/Modelo:\s*([\w\/]+)/),
    'Zero KM':          get(tVeic, /Zero KM:\s*(Sim|Não)/i),
    'Placa':            get(tVeic + ' ' + tCot, /Placa[:\s]+(.+?)(?=\s{3,}|\s+(?:Chassi|FIPE|Combustível|Fabricação|Zero KM|CEP Pernoite|Financiado)|$)/i),
    'Chassi':           get(tVeic, /Chassi:\s*([A-Z0-9]+)/),
    'FIPE':             get(tVeic, /FIPE:\s*([\d\-]+)/),
    'Combustível':      get(tVeic, /Combustível:\s*(\w+)/),
    'CEP Pernoite':     get(tVeic, /CEP Pernoite:\s*([\d\-]+)/),
    'Financiado':       get(tVeic, /Financiado:\s*(Sim|Não)/i),
  }

  const endCoberturas = iFranquia !== -1 ? iFranquia : (iValor !== -1 ? iValor : rows.length)
  const endFranquia   = iValor !== -1 ? iValor : rows.length

  // Calibra splits usando a linha FRANQUIA — detecta automaticamente 1, 2 ou 3 seguradoras
  let splits = [200, 400] // fallback para 2 segs
  if (iFranquia !== -1) {
    const fr = rows[iFranquia]
    const nSegs = fr.cells.length - 1 // células menos o label = nº de seguradoras
    if (nSegs >= 1) {
      splits = []
      for (let i = 0; i < nSegs; i++) {
        splits.push((fr.cells[i].x + fr.cells[i + 1].x) / 2)
      }
    }
  }

  const tabelaCoberturas = iCoberturas !== -1 ? parsearTabelaNCol(rows, iCoberturas, endCoberturas, splits) : []
  const tabelaFranquia   = iFranquia !== -1   ? parsearTabelaNCol(rows, iFranquia,   endFranquia,   splits) : []

  const yCoberturas = iCoberturas !== -1 ? rows[iCoberturas].y : null

  return { dadosCotacao, perfilCondutor, dadosVeiculo, tabelaCoberturas, tabelaFranquia, _splits: splits, _yCoberturas: yCoberturas }
}

// ── Gera o arquivo Excel no browser ─────────────────────────────────────────
function _buildWbCotacao(dados) {
  const { dadosCotacao, perfilCondutor, dadosVeiculo, tabelaCoberturas, tabelaFranquia } = dados
  const aoa = []
  const addSecao = titulo => { aoa.push([]); aoa.push([titulo]) }
  const addKV    = obj    => Object.entries(obj).forEach(([k, v]) => { if (v) aoa.push([k, v]) })
  addSecao('DADOS DA COTAÇÃO');   addKV(dadosCotacao)
  addSecao('PERFIL DO CONDUTOR'); addKV(perfilCondutor)
  addSecao('DADOS DO VEÍCULO');   addKV(dadosVeiculo)
  addSecao('COBERTURAS');         tabelaCoberturas.forEach(r => aoa.push(r))
  addSecao('FRANQUIA');           tabelaFranquia.forEach(r => aoa.push(r))
  const ws = XLSX.utils.aoa_to_sheet(aoa)
  const nCols = Math.max(...aoa.map(r => r.length), 3)
  ws['!cols'] = [{ wch: 36 }, ...Array(nCols - 1).fill({ wch: 46 })]
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Cotação')
  return wb
}

function gerarExcelCotacao(dados, nomeArquivo = 'cotacao.xlsx') {
  XLSX.writeFile(_buildWbCotacao(dados), nomeArquivo)
}

function gerarExcelCotacaoEmMemoria(dados) {
  const buf = XLSX.write(_buildWbCotacao(dados), { bookType: 'xlsx', type: 'array' })
  return new File([buf], 'cotacao.xlsx', { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}

// ── Identifica seguradora pela logo da página (dimensões únicas por seguradora) ──
async function identificarSeguradoraPorLogo(page) {
  // Mapeamento: "LARGURAxALTURA" → nome da seguradora
  // Dimensões coletadas empiricamente dos PDFs de parcelamento reais
  const LOGO_DIMS = {
    '120x44':  'HDI Seguros',
    '422x117': 'Bradesco',
    '120x30':  'Tokio Marine',
    '123x50':  'Suhai',
  }
  try {
    const opList = await page.getOperatorList()
    const imageNames = []
    for (let i = 0; i < opList.fnArray.length; i++) {
      if (opList.fnArray[i] === pdfjsLib.OPS.paintImageXObject) {
        const name = opList.argsArray[i][0]
        if (!imageNames.includes(name)) imageNames.push(name)
      }
    }
    for (const name of imageNames) {
      const img = await new Promise(resolve => {
        try { page.objs.get(name, obj => resolve(obj)) } catch(e) { resolve(null) }
      })
      if (img && img.width && img.height) {
        const key = `${img.width}x${img.height}`
        if (LOGO_DIMS[key]) return LOGO_DIMS[key]
      }
    }
  } catch (_) { /* silencioso — cai no fallback de texto */ }
  return ''
}

// ── Parsa uma página do PDF de parcelamento — retorna array (1 item por tabela encontrada) ──
function parsearPaginaParcelamento(rawItems, logoSeg = '') {
  const valid = rawItems.filter(i => i.str.trim()).map(i => ({ str: i.str.trim(), x: i.transform[4], y: i.transform[5] }))
  const SEGS = ['Aliro Seguro','Aliro','Yelum','Porto Seguro','HDI Seguros','Tokio Marine','Azul Seguros','Bradesco','Suhai','Akad','Essor','Mitsui','Itaú','Itau']
  const Y_TOL = 12

  // Normaliza string para comparação: remove acentos, maiúsculas
  const norm = s => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().trim()
  const PAY_NORM = { 'DEBITO': 'DÉBITO', 'CREDITO': 'CRÉDITO', 'BOLETO': 'BOLETO' }
  const SEG_ALIASES = [
    { nome: 'Mitsui', clues: ['35 - REDE REFERENCIADA - KM'] },
    { nome: 'Allianz', clues: ['KM LIVRE', 'VIDROS, FAROIS, LANTERNAS E RETROVISORES'] },
    { nome: 'Itaú', clues: ['ITAU ESSENCIAL', ' ITAU '] },
    { nome: 'Bradesco', clues: ['VIDRO PROTEGIDO PLUS', 'AUTO RESERVA 07 DIAS', 'ASSIST. DIA/NOITE ILIMITADO', 'ASSIST. DIA/NOITE'] },
    { nome: 'Porto Seguro', clues: ['PORTO SEGURO', '31 - ESSENCIAL - ASSISTENCIA KM', 'SERVICOS A RESIDENCIA'] },
  ]

  const inferirSeguradora = (items, fallback = '') => {
    const anteriorLabel = items.find(i => norm(i.str).startsWith('SEGURADORA ANTERIOR'))
    const itensDetect = anteriorLabel
      ? items.filter(i => !(Math.abs(i.y - anteriorLabel.y) < 20 && i.x >= anteriorLabel.x - 10))
      : items
    const joined = ` ${itensDetect.map(i => norm(i.str)).join(' ')} `

    for (const { nome, clues } of SEG_ALIASES) {
      if (clues.some(clue => joined.includes(clue))) return nome
    }
    for (const s of SEGS) {
      const sNorm = norm(s)
      if (itensDetect.some(i => norm(i.str) === sNorm || norm(i.str).startsWith(sNorm + ' ') || norm(i.str).includes(sNorm))) {
        return s === 'Itau' ? 'Itaú' : s
      }
    }
    for (const s of SEGS) {
      const sNorm = norm(s)
      if (itensDetect.some(i => sNorm.startsWith(norm(i.str) + ' ') && i.str.length >= 3)) {
        return s === 'Itau' ? 'Itaú' : s
      }
    }
    return fallback
  }

  const extrairNumeroCotacao = items => {
    const ordered = [...items].sort((a, b) => a.y - b.y || a.x - b.x)
    for (let idx = 0; idx < ordered.length; idx++) {
      const cur = ordered[idx].str
      const curNorm = norm(cur)
      const inline = cur.match(/N[oº°O]\s*(\d+(?:-\d+)?)/) || cur.match(/^(\d+(?:-\d+)?)$/)
      if (inline && inline[1].replace(/\D/g, '').length >= 9) return inline[1]
      if (curNorm === 'Nº' || curNorm === 'NO' || curNorm === 'N°' || curNorm === 'N') {
        const next = ordered[idx + 1]?.str || ''
        const split = next.match(/^(\d+(?:-\d+)?)$/)
        if (split && split[1].replace(/\D/g, '').length >= 9) return split[1]
      }
    }
    return ''
  }

  // Agrupa cabeçalhos de pagamento por nível Y — cada grupo = uma tabela de seguradora
  // Suporta páginas sem DÉBITO (ex: seguradora que só oferece CRÉDITO/BOLETO)
  const allPayHeaders = valid
    .filter(i => PAY_NORM[norm(i.str)])
    .map(i => ({ ...i, type: PAY_NORM[norm(i.str)] }))
  if (!allPayHeaders.length) return []

  const tableYs = []
  for (const h of allPayHeaders.sort((a, b) => b.y - a.y)) {
    if (!tableYs.some(y => Math.abs(y - h.y) < Y_TOL)) tableYs.push(h.y)
  }

  return tableYs.map((headerY, idx) => {
    // Colunas presentes, ordenadas por X (esquerda → direita), usando normalização
    const TYPE_KEY = { 'DÉBITO': 'debito', 'CRÉDITO': 'credito', 'BOLETO': 'boleto' }
    const headersAtY = allPayHeaders.filter(i => Math.abs(i.y - headerY) < Y_TOL)
    const cols = ['DÉBITO','CRÉDITO','BOLETO']
      .map(tipo => {
        const h = headersAtY.find(i => i.type === tipo)
        return h ? { type: TYPE_KEY[tipo], x: h.x } : null
      })
      .filter(Boolean)
      .sort((a, b) => a.x - b.x)

    const mids = cols.slice(0, -1).map((c, i) => (c.x + cols[i + 1].x) / 2)
    const getColIdx = x => { let ci = 0; while (ci < mids.length && x >= mids[ci]) ci++; return ci }

    const floorY = idx + 1 < tableYs.length ? tableYs[idx + 1] : -Infinity
    const tableItems = valid.filter(i => i.y < headerY && i.y > floorY).sort((a, b) => b.y - a.y || a.x - b.x)

    const colData = cols.map((_, ci) => {
      const colItems = tableItems.filter(i => getColIdx(i.x) === ci)
      const rows = []
      for (let i = 0; i < colItems.length; i++) {
        // Aceita: "Nx sem juros de R$ X", "Nx com juros de R$ X", "Nx s/juros R$ X", "Nx c/juros R$ X"
        const m = colItems[i].str.match(/^(\d+)x (sem juros(?: de)?|com juros(?: de)?|s\/juros|c\/juros) R\$\s*([\d.,]+)$/)
        if (m) {
          const isSem = m[2].startsWith('sem') || m[2] === 's/juros'
          const val = m[3]
          let total = ''
          if (i + 1 < colItems.length) { const tm = colItems[i+1].str.match(/^R\$\s*([\d.,]+)$/); if (tm) { total = `R$ ${tm[1]}`; i++ } }
          rows.push({ label: `${m[1]}x ${isSem ? 's/juros' : 'c/juros'} R$ ${val}`, total })
        }
      }
      return rows
    })
    const getColData = type => { const ci = cols.findIndex(c => c.type === type); return ci >= 0 ? colData[ci] : [] }

    const ceilY = idx > 0 ? tableYs[idx - 1] : Infinity
    const above = valid.filter(i => i.y > headerY && i.y < ceilY)
    const aboveSemAnt = above.filter(i => !i.str.toUpperCase().includes('ANTERIOR'))

    // Prioriza pistas textuais da própria página; usa hint do backend só como fallback.
    let seguradora = inferirSeguradora([...aboveSemAnt, ...tableItems], '') || inferirSeguradora(aboveSemAnt, '') || logoSeg

    const numero = extrairNumeroCotacao(above)

    // Na mesma linha do PREÇO costuma haver a FRANQUIA (outra coluna à direita); um filtro só por
    // "x > precoLabel.x - 20" sem limite superior pegava os dígitos da FRANQUIA junto (ex.: valor
    // saía como "R$ ,82" em vez de "R$ 2.701,82"). Atribui cada item ao rótulo em maiúsculas mais
    // próximo em X, na mesma linha, e ordena só por X (o valor é sempre uma única linha, mas os
    // glifos do "R$", do inteiro e da parte decimal às vezes vêm com baseline/Y levemente diferente).
    const precoLabel = above.find(i => i.str === 'PREÇO')
    let preco = ''
    if (precoLabel) {
      const rowLabels = above.filter(i => /^[A-ZÀ-Ú]{3,}/.test(i.str) && i.str === i.str.toUpperCase() && Math.abs(i.y - precoLabel.y) < 12)
      const precoItems = above.filter(i =>
        i.str !== 'PREÇO' &&
        Math.abs(i.y - precoLabel.y) < 30 &&
        i.x > precoLabel.x - 30 &&
        (!rowLabels.length || rowLabels.reduce((a, b) => Math.abs(b.x - i.x) < Math.abs(a.x - i.x) ? b : a) === precoLabel)
      )
      const precoRaw = precoItems.sort((a, b) => a.x - b.x).map(i => i.str).join('').trim()
      const mPreco = precoRaw.match(/R\$\s*[\d.,]+/)
      preco = mPreco ? mPreco[0] : precoRaw
    }

    const franquiaLabel = above.find(i => i.str === 'FRANQUIA')
    let franquiaValor = '', franquiaTipo = ''
    if (franquiaLabel) {
      const area = above.filter(i => i.str !== 'FRANQUIA' && Math.abs(i.y - franquiaLabel.y) < 40 && i.x > franquiaLabel.x - 20)
      franquiaValor = area.filter(i => /R\$/.test(i.str) || /^\d/.test(i.str)).map(i => i.str).join('').trim()
      franquiaTipo  = area.filter(i => i.str.includes('%') || i.str.toLowerCase().includes('obrig')).map(i => i.str).join(' ').trim()
    }

    return { seguradora, numero, preco, franquiaValor, franquiaTipo, debito: getColData('debito'), credito: getColData('credito'), boleto: getColData('boleto') }
  })
}

async function parsearParcelamentoPDF(file) {
  const buffer = await file.arrayBuffer()
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise

  // Identifica seguradoras via backend (Google Vision) — evita hang do identificarSeguradoraPorLogo
  let logosPorPagina = []
  try {
    const mesclarUrl = process.env.REACT_APP_MESCLAR_PDF_URL || 'https://corretora-mesclar-pdf.onrender.com'
    const fd = new FormData()
    fd.append('parcelamento', file)
    const res = await fetch(`${mesclarUrl}/identificar-parcelamento`, { method: 'POST', body: fd })
    if (res.ok) logosPorPagina = await res.json()
  } catch (_) {}

  const paginas = []
  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p)
    const content = await page.getTextContent()
    const logoSeg = logosPorPagina[p - 1] || ''
    const dados = parsearPaginaParcelamento(content.items, logoSeg)
    paginas.push(...dados)
  }
  return paginas
}

function gerarPDFParcelamento(paginas) {
  const C = { blue: 'var(--primary)', light: 'var(--primary-light)', border: '#FFD5C8', text: '#1A1A2E', label: '#444', rowEven: '#FFF8F6' }
  const hoje = new Date().toLocaleDateString('pt-BR')

  const buildTabela = pg => {
    const cols = [
      pg.debito.length  ? { titulo: 'DÉBITO',  rows: pg.debito  } : null,
      pg.credito.length ? { titulo: 'CRÉDITO', rows: pg.credito } : null,
      pg.boleto.length  ? { titulo: 'BOLETO',  rows: pg.boleto  } : null,
    ].filter(Boolean)
    if (!cols.length) return ''

    const colPct = Math.floor(90 / cols.length)
    const thStyle = `padding:6px 10px;font-size:11px;font-weight:700;color:#fff;background:${C.blue};text-align:center;`
    const tdLStyle = `padding:5px 8px;font-size:10.5px;color:${C.label};border-bottom:1px solid ${C.border};`
    const tdVStyle = `padding:5px 8px;font-size:10.5px;color:${C.text};font-weight:600;text-align:right;border-bottom:1px solid ${C.border};`

    const maxRows = Math.max(...cols.map(c => c.rows.length))

    let html = `<table style="width:100%;border-collapse:collapse;margin-top:6px;">`
    // cabeçalho de tipos
    html += `<tr><td style="width:10%;"></td>`
    cols.forEach(() => { html += `<th colspan="2" style="${thStyle}width:${colPct}%;"></th>` })
    html += `</tr><tr><td style="width:10%;padding:4px 8px;font-size:10px;font-weight:700;color:${C.label};">Parcelas</td>`
    cols.forEach(c => {
      html += `<th style="${thStyle}width:${Math.floor(colPct/2)}%;">${c.titulo}</th>`
      html += `<th style="${thStyle}width:${Math.ceil(colPct/2)}%;font-size:10px;">Total</th>`
    })
    html += `</tr>`

    for (let r = 0; r < maxRows; r++) {
      const bg = r % 2 === 0 ? '#fff' : C.rowEven
      // label da parcela vem da primeira coluna disponível
      const anyRow = cols.map(c => c.rows[r]).find(Boolean)
      const parcelLabel = anyRow ? anyRow.label.replace(/^(\d+)x (s\/juros|c\/juros) R\$\s*[\d.,]+$/, '$1x $2') : ''
      html += `<tr style="background:${bg}">`
      html += `<td style="${tdLStyle}font-size:10px;">${escapeHtml(parcelLabel)}</td>`
      cols.forEach(c => {
        const row = c.rows[r] || { label: '', total: '' }
        // valor unitário da parcela (última parte do label)
        const parcVal = row.label ? row.label.replace(/^\d+x (?:s|c)\/juros /, '') : '—'
        html += `<td style="${tdLStyle}text-align:right;">${row.label ? escapeHtml(parcVal) : '—'}</td>`
        html += `<td style="${tdVStyle}">${escapeHtml(row.total) || (row.label ? '—' : '')}</td>`
      })
      html += `</tr>`
    }
    html += `</table>`
    return html
  }

  const segCards = paginas.map(pg => {
    const nome = escapeHtml(pg.seguradora) || 'Seguradora'
    const num  = pg.numero ? ` &nbsp; Nº ${escapeHtml(pg.numero)}` : ''
    const preco   = pg.preco      ? `<span style="margin-left:16px;font-size:12px;color:${C.text};">Preço: <b>${escapeHtml(pg.preco)}</b></span>` : ''
    const franquia = (pg.franquiaValor || pg.franquiaTipo)
      ? `<span style="margin-left:16px;font-size:11px;color:#666;">Franquia: ${escapeHtml(pg.franquiaValor)} ${escapeHtml(pg.franquiaTipo)}</span>` : ''
    return `<div style="margin-bottom:20px;border:1px solid ${C.border};border-radius:6px;overflow:hidden;">
      <div style="background:${C.light};padding:10px 14px;display:flex;align-items:center;flex-wrap:wrap;gap:4px;">
        <span style="font-size:13px;font-weight:700;color:${C.blue};">${nome}${num}</span>
        ${preco}${franquia}
      </div>
      <div style="padding:0 12px 12px;">${buildTabela(pg)}</div>
    </div>`
  }).join('')

  const html = `<!DOCTYPE html><html><head>
<meta charset="utf-8"><title>Parcelamento</title>
<style>
  body{font-family:Arial,sans-serif;margin:0;padding:0;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
  .page{max-width:820px;margin:0 auto;padding:20px;}
  .hdr{background:${C.blue};color:#fff;padding:14px 20px;border-radius:6px;margin-bottom:18px;display:flex;justify-content:space-between;align-items:center;}
  .hdr h1{margin:0;font-size:16px;font-weight:700;}
  .footer{text-align:center;font-size:10px;color:#aaa;margin-top:16px;border-top:1px solid #eee;padding-top:10px;}
  @media print{@page{size:A4;margin:10mm;}.page{padding:0;}}
</style></head><body>
<div class="page">
  <div class="hdr"><h1>PARCELAMENTO</h1><div style="font-size:11px;opacity:.85;">${hoje}</div></div>
  ${segCards}
  <div class="footer">Documento gerado pelo sistema Corretora &mdash; ${hoje}</div>
</div>
<script>setTimeout(()=>window.print(),400)</script>
</body></html>`

  const win = window.open('', '_blank')
  if (win) { win.document.write(html); win.document.close() }
  else avisar('Permita pop-ups para gerar o PDF.')
}

function _buildWbParcelamento(paginas) {
  const aoa = []
  paginas.forEach((pg, idx) => {
    if (idx > 0) { aoa.push([]); aoa.push([]) }
    const cabecalho = [pg.seguradora || 'Seguradora', pg.numero ? `Nº ${pg.numero}` : ''].filter(Boolean).join(' ')
    aoa.push([cabecalho, '', '', `Preço: ${pg.preco}`, '', `Franquia: ${pg.franquiaValor} ${pg.franquiaTipo}`])
    aoa.push([])
    aoa.push(['DÉBITO', '', 'CRÉDITO', '', 'BOLETO', ''])
    aoa.push(['Parcelamento', 'Total', 'Parcelamento', 'Total', 'Parcelamento', 'Total'])
    // Alinha Débito/Crédito/Boleto pelo número real da parcela (não pela posição no array),
    // pois seguradoras como Porto/Azul/Itaú/Mitsui não oferecem Boleto à vista (1x) — o Boleto
    // começa direto em 2x, e alinhar por posição deslocava os valores do Boleto uma linha para cima.
    const nDoItem = item => {
      const txt = (item && (item.parcelamento !== undefined ? item.parcelamento : item.label)) || ''
      const m = String(txt).match(/^(\d+)x/)
      return m ? parseInt(m[1], 10) : null
    }
    const porN = arr => {
      const mapa = {}
      arr.forEach(item => { const n = nDoItem(item); if (n) mapa[n] = item })
      return mapa
    }
    const dPorN = porN(pg.debito)
    const cPorN = porN(pg.credito)
    const bPorN = porN(pg.boleto)
    const maxN = Math.max(0, ...Object.keys(dPorN).map(Number), ...Object.keys(cPorN).map(Number), ...Object.keys(bPorN).map(Number))
    for (let n = 1; n <= maxN; n++) {
      const d = dPorN[n] || { label: '', total: '' }
      const c = cPorN[n] || { label: '', total: '' }
      const b = bPorN[n] || { label: '', total: '' }
      const dLabel = d.parcelamento !== undefined ? d.parcelamento : (d.label || '')
      const cLabel = c.parcelamento !== undefined ? c.parcelamento : (c.label || '')
      const bLabel = b.parcelamento !== undefined ? b.parcelamento : (b.label || '')
      aoa.push([dLabel, d.total, cLabel, c.total, bLabel, b.total])
    }
    aoa.push(['Obs:', pg.observacoes || '', '', '', '', ''])
  })
  const ws = XLSX.utils.aoa_to_sheet(aoa)
  ws['!cols'] = [{ wch: 30 }, { wch: 13 }, { wch: 30 }, { wch: 13 }, { wch: 30 }, { wch: 13 }]
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Parcelamento')
  return wb
}

function gerarExcelParcelamento(paginas, nomeArquivo = 'parcelamento.xlsx') {
  XLSX.writeFile(_buildWbParcelamento(paginas), nomeArquivo)
}

function gerarExcelParcelamentoEmMemoria(paginas) {
  const buf = XLSX.write(_buildWbParcelamento(paginas), { bookType: 'xlsx', type: 'array' })
  return new File([buf], 'parcelamento.xlsx', { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}

const COLUNAS = [
  { id: 'prospeccao',      label: 'Novo Card (Oportunidade)', cor: '#6B7280' },
  { id: 'primeiro_contato',label: 'Calculando',               cor: '#FFA500' },
  { id: 'cotacao',         label: 'Cotação Enviada',          cor: '#FF4500' },
  { id: 'proposta_enviada',label: 'Em Negociação',            cor: '#0000CD' },
  { id: 'negociacao',      label: 'Fechado',                  cor: '#008000' },
  { id: 'fechamento',      label: 'Perdido',                  cor: '#B22222' },
]

const RAMOS = ['Acidentes Pessoais','Automóvel','Bike','Carta Verde','Condomínio','Empresarial','Equipamentos','Residencial','Responsabilidade Civil','Vida em Grupo','Vida Individual','Viagem']
const TIPOS = ['Novo', 'Renovação Interna', 'Endosso', 'Refeito', 'Negociação Perdido', 'Renovação Perdida']
// Tipos que ainda nao tem regra definida de vinculo com apolice — por enquanto tratados igual a
// "Novo" (bloco "Apólice Vinculada" fica oculto). Configurar depois, a pedido do usuario.
const TIPOS_SEM_APOLICE_VINCULADA = ['Novo', 'Negociação Perdido', 'Renovação Perdida']
// Card movido para "Perdido": motivo (obrigatorio) + se quer retornar o contato no proximo ano
// (pedido do usuario, 26/10). Fica no Historico de Atendimento e na tela Perdidos; com "Sim" o
// usuario escolhe a data do retorno (sem lembrete na Agenda — pedido do usuario, 26/10).
const MOTIVOS_PERDA = ['Preço muito alto', 'Fechou com outra corretora', 'Fechou no Banco', 'Vendeu o veículo', 'Sem retorno do cliente', 'Desistiu do seguro', 'Outro']
const PERDA_VAZIA = { motivo: '', obs: '', retornar: '', dataRetorno: '' }
const hojeISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` }
const fmtDataBR = (iso) => iso.split('-').reverse().join('/')
const MODAL_VAZIO = { cliente_id: '', etapa: 'prospeccao', ramo: 'Automóvel', tipo: 'Novo', observacoes: '', apolice_origem_id: null }

// Extrai tabelas DÉBITO/CRÉDITO/BOLETO de uma página do PDF parcelamento
const parsearTabelaParcelamento = (items) => {
  const valid = items.filter(i => i.str.trim()).map(i => ({ str: i.str.trim(), x: i.transform[4], y: i.transform[5] }))
  const debitoItem  = valid.find(i => i.str === 'DÉBITO')
  const creditoItem = valid.find(i => i.str === 'CRÉDITO')
  const boletoItem  = valid.find(i => i.str === 'BOLETO')
  if (!debitoItem) return null
  const c1x = debitoItem.x, c2x = creditoItem?.x ?? c1x + 150, c3x = boletoItem?.x ?? c2x + 150
  const mid12 = (c1x + c2x) / 2, mid23 = (c2x + c3x) / 2
  const headerY = debitoItem.y
  const getCol = (x) => x < mid12 ? 1 : x < mid23 ? 2 : 3
  const tableItems = valid.filter(i => i.y < headerY).sort((a, b) => b.y - a.y || a.x - b.x)
  const parseCol = (colNum) => {
    const colItems = tableItems.filter(i => getCol(i.x) === colNum)
    const rows = []
    for (let i = 0; i < colItems.length; i++) {
      const m = colItems[i].str.match(/^(\d+)x (sem|com) juros de R\$\s*([\d.,]+)$/)
      if (m) {
        let total = ''
        if (i + 1 < colItems.length) { const tm = colItems[i+1].str.match(/^R\$\s*([\d.,]+)$/); if (tm) { total = `R$ ${tm[1]}`; i++ } }
        rows.push({ label: `${m[1]}x ${m[2]==='sem'?'s/juros':'c/juros'} R$ ${m[3]}`, total })
      }
    }
    return rows
  }
  const above = valid.filter(i => i.y > headerY)
  const SEGS = ['Aliro Seguro','Aliro','Yelum','Porto Seguro','HDI Seguros','Tokio Marine','Azul Seguros','Bradesco','Suhai','Akad','Essor','Mitsui','Itaú','Itau']
  // Ignora itens que fazem parte de "SEGURADORA ANTERIOR" ou "Seguradora Anterior"
  const aboveSemAnterior = above.filter(i => !i.str.includes('ANTERIOR') && !i.str.includes('Anterior') && !i.str.includes('anterior'))
  let seguradora = ''
  // Procura primeiro por nome exato (não parte de outra palavra)
  for (const s of SEGS) { if (aboveSemAnterior.some(i => i.str === s || i.str.startsWith(s + ' ') || i.str.endsWith(' ' + s))) { seguradora = s; break } }
  if (!seguradora) { for (const s of SEGS) { if (aboveSemAnterior.some(i => i.str.includes(s))) { seguradora = s; break } } }
  let numero = ''
  const aboveOrdenado = [...above].sort((a, b) => a.y - b.y || a.x - b.x)
  for (let idx = 0; idx < aboveOrdenado.length; idx++) {
    const cur = aboveOrdenado[idx].str
    const curNorm = norm(cur)
    const m = cur.match(/N[oº°O]\s*(\d+(?:-\d+)?)/) || cur.match(/^(\d+(?:-\d+)?)$/)
    if (m && m[1].replace(/\D/g, '').length >= 9) { numero = m[1]; break }
    if (curNorm === 'Nº' || curNorm === 'NO' || curNorm === 'N°' || curNorm === 'N') {
      const next = aboveOrdenado[idx + 1]?.str || ''
      const split = next.match(/^(\d+(?:-\d+)?)$/)
      if (split && split[1].replace(/\D/g, '').length >= 9) { numero = split[1]; break }
    }
  }
  return { seguradora, numero, debito: parseCol(1), credito: parseCol(2), boleto: parseCol(3) }
}

// Desenha tabelas DÉBITO/CRÉDITO/BOLETO no canvas
const desenharTabelasCanvas = (ctx, tabelas, startY, canvasW, scale) => {
  const pad = 28 * scale, usableW = canvasW - 2 * pad, colW = usableW / 3, lh = 13.5 * scale
  let y = startY + 6 * scale
  ctx.save()
  tabelas.filter(Boolean).forEach((tab, ti) => {
    if (ti > 0) y += 10 * scale
    const label = [tab.seguradora, tab.numero ? `Nº ${tab.numero}` : ''].filter(Boolean).join('  ')
    ctx.fillStyle = corPrimariaResolvida(); ctx.font = `bold ${8.5 * scale}px Arial`; ctx.textAlign = 'left'
    ctx.fillText(label, pad, y + 10 * scale); y += 18 * scale
    ;[0,1,2].forEach(ci => { ctx.fillStyle = '#FFD5C8'; ctx.fillRect(pad + ci*colW, y, colW-2, 15*scale) })
    ;['DÉBITO','CRÉDITO','BOLETO'].forEach((h, ci) => {
      ctx.fillStyle = corPrimariaResolvida(); ctx.font = `bold ${8.5 * scale}px Arial`; ctx.textAlign = 'center'
      ctx.fillText(h, pad + ci*colW + colW/2, y + 11*scale)
    })
    y += 16 * scale
    ;[0,1,2].forEach(ci => {
      ctx.fillStyle = '#888'; ctx.font = `${7*scale}px Arial`; ctx.textAlign = 'left'
      ctx.fillText('Parcelamento', pad + ci*colW + 3, y + 9*scale)
      ctx.fillText('Total', pad + ci*colW + colW*0.63, y + 9*scale)
    })
    y += 13 * scale
    const cols = [tab.debito, tab.credito, tab.boleto]
    const maxRows = Math.max(...cols.map(c => c.length))
    for (let r = 0; r < maxRows; r++) {
      ;[0,1,2].forEach(ci => {
        ctx.fillStyle = r%2===0 ? '#FFFFFF' : '#F5F8FC'; ctx.fillRect(pad + ci*colW, y, colW-2, lh)
        const row = cols[ci][r]
        if (row) {
          ctx.fillStyle = '#222'; ctx.font = `${7.5*scale}px Arial`; ctx.textAlign = 'left'
          ctx.fillText(row.label, pad + ci*colW + 3, y + 9.5*scale)
          ctx.fillText(row.total, pad + ci*colW + colW*0.63, y + 9.5*scale)
        }
      })
      y += lh
    }
  })
  ctx.textAlign = 'left'; ctx.restore()
}

const iconeArquivo = (nome) => {
  const ext = (nome || '').split('.').pop().toLowerCase()
  if (ext === 'pdf') return '📕'
  if (['jpg', 'jpeg', 'png'].includes(ext)) return '🖼️'
  return '📄'
}

// Anexos do card do Funil — mesmo padrao de anexo usado em Apolices.jsx (base64 via JSON,
// arquivo salvo em disco no backend), so que sem o campo "tipo" (aqui e so anexo generico).
// Ao escolher o arquivo ja sobe direto, sem passo extra de confirmar/salvar.
function blobParaDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = e => resolve(e.target.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

// Nome do anexo da Cotação PDF: "<Segurado> - Cotação N.pdf", com N sendo o próximo número
// disponível pra essa negociação (conta os anexos já existentes que batem com esse padrão).
// funilId nulo (negociação ainda não salva) sempre começa em 1.
async function nomearAnexoCotacao(funilId, segurado) {
  const base = (segurado || 'Cliente').trim()
  let proximo = 1
  if (funilId) {
    try {
      const { data } = await api.get(`/funil/${funilId}/anexos`)
      const nums = (Array.isArray(data) ? data : [])
        .map(a => String(a.nome || '').match(/Cota[çc][ãa]o (\d+)\.pdf$/i))
        .filter(Boolean).map(m => parseInt(m[1], 10))
      if (nums.length) proximo = Math.max(...nums) + 1
    } catch (_) { /* se falhar, comeca em 1 mesmo */ }
  }
  return `${base} - Cotação ${proximo}.pdf`
}

function FunilAnexos({ funilId, refreshSignal }) {
  const [anexos, setAnexos] = useState([])
  const [enviando, setEnviando] = useState(false)

  const carregar = () => {
    api.get(`/funil/${funilId}/anexos`).then(r => setAnexos(Array.isArray(r.data) ? r.data : [])).catch(() => setAnexos([]))
  }
  // refreshSignal: incrementado pelo pai quando um anexo e enviado por fora deste componente
  // (ex.: Cotação PDF gerada e anexada automaticamente), pra recarregar a lista sem trocar de tela.
  useEffect(() => { carregar() }, [funilId, refreshSignal])

  const enviarArquivo = async (arquivo) => {
    setEnviando(true)
    try {
      const dados = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = e => resolve(e.target.result)
        reader.onerror = reject
        reader.readAsDataURL(arquivo)
      })
      await api.post(`/funil/${funilId}/anexos`, { nome: arquivo.name, tamanho: arquivo.size, dados })
      carregar()
    } catch (err) {
      avisar('Erro ao enviar anexo: ' + (err.response?.data?.erro || err.message))
    } finally {
      setEnviando(false)
    }
  }

  const remover = async (id) => {
    if (!(await confirmar('Remover este anexo?'))) return
    await api.delete(`/funil/anexos/${id}`)
    carregar()
  }

  const visualizar = async (a) => {
    try {
      const { data } = await api.get(`/funil/anexos/${a.id}/dados`)
      const [cabecalho, base64] = data.dados.split(',')
      const mime = cabecalho.split(':')[1].split(';')[0]
      const bin = atob(base64)
      const bytes = new Uint8Array(bin.length)
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
      const url = URL.createObjectURL(new Blob([bytes], { type: mime }))
      window.open(url, '_blank')
    } catch (err) {
      avisar('Erro ao abrir anexo: ' + (err.response?.data?.erro || err.message))
    }
  }

  return (
    <div className="form-group">
      <label className="form-label">Anexos</label>
      {anexos.length === 0 ? (
        <div style={{ padding: 14, textAlign: 'center', color: '#aaa', background: '#f8f9fa', borderRadius: 8, marginBottom: 10, fontSize: 12 }}>
          Nenhum anexo adicionado ainda.
        </div>
      ) : (
        <div style={{ marginBottom: 10 }}>
          {anexos.map(a => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f0f0f0', fontSize: 12 }}>
              <span>{iconeArquivo(a.nome)}</span>
              <button type="button" onClick={() => visualizar(a)} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0, flex: 1, textAlign: 'left',
                color: 'var(--primary)', textDecoration: 'underline', fontFamily: 'inherit', wordBreak: 'break-all'
              }}>{a.nome}</button>
              <button type="button" className="btn btn-danger btn-sm" onClick={() => remover(a.id)}>Remover</button>
            </div>
          ))}
        </div>
      )}
      <label className="btn btn-secondary" style={{ display: 'inline-flex', cursor: enviando ? 'wait' : 'pointer', fontSize: 13 }}>
        {enviando ? 'Enviando...' : '+ Adicionar anexo'}
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" disabled={enviando} style={{ display: 'none' }}
          onChange={e => { const arquivo = e.target.files[0]; e.target.value = ''; if (arquivo) enviarArquivo(arquivo) }} />
      </label>
    </div>
  )
}

// Campo + botão de registrar um atendimento (usado tanto na aba Dados quanto no topo do
// Histórico de Atendimento) — sempre grava em funil_historico; quem exibe a lista e o
// FunilHistorico, que recarrega via onRegistrado quando esse form esta dentro dele.
function RegistrarAtendimento({ funilId, onRegistrado }) {
  const [texto, setTexto] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [salvo, setSalvo] = useState(false)

  const registrar = async () => {
    if (!texto.trim()) return
    setEnviando(true)
    try {
      await api.post(`/funil/${funilId}/historico`, { texto })
      setTexto('')
      setSalvo(true)
      setTimeout(() => setSalvo(false), 2500)
      onRegistrado?.()
    } catch (err) {
      avisar('Erro ao salvar: ' + (err.response?.data?.erro || err.message))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="form-group">
      <label className="form-label">Registrar atendimento</label>
      <textarea
        className="form-input" rows={2} style={{ resize: 'vertical' }}
        placeholder="Ligação, e-mail, reunião..."
        value={texto} onChange={e => setTexto(e.target.value)}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 6 }}>
        <button type="button" className="btn btn-primary btn-sm" onClick={registrar} disabled={enviando || !texto.trim()}>
          {enviando ? 'Registrando...' : 'Registrar'}
        </button>
        {salvo && <span style={{ fontSize: 12, color: '#3B6D11' }}>✓ Atendimento registrado</span>}
      </div>
    </div>
  )
}

// Histórico de Atendimento: anotações manuais do corretor sobre contatos com o cliente
// (ligação, e-mail, reunião...), mais recente primeiro.
function FunilHistorico({ funilId }) {
  const [itens, setItens] = useState([])
  const [carregando, setCarregando] = useState(true)

  const carregar = () => {
    setCarregando(true)
    api.get(`/funil/${funilId}/historico`)
      .then(r => setItens(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItens([]))
      .finally(() => setCarregando(false))
  }
  useEffect(() => { carregar() }, [funilId])

  const remover = async (id) => {
    if (!(await confirmar('Remover esta anotação?'))) return
    await api.delete(`/funil/historico/${id}`)
    carregar()
  }

  const fmtDataHora = (iso) => new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  })

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <RegistrarAtendimento funilId={funilId} onRegistrado={carregar} />

      {carregando ? (
        <div style={{ textAlign: 'center', color: '#aaa', padding: 20, fontSize: 13 }}>Carregando...</div>
      ) : itens.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#aaa', background: '#f8f9fa', borderRadius: 8, fontSize: 13 }}>
          Nenhum atendimento registrado ainda.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 2 }}>
          {itens.map(h => (
            <div key={h.id} style={{ position: 'relative', padding: '10px 28px 10px 14px', borderRadius: 8, border: '1px solid #eee', background: '#fafafa' }}>
              <button type="button" onClick={() => remover(h.id)} title="Remover" style={{
                position: 'absolute', top: 8, right: 8,
                background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: 14, lineHeight: 1
              }}>×</button>
              <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 4px' }}>
                {fmtDataHora(h.created_at)}{h.usuario_nome ? ` · ${h.usuario_nome}` : ''}
              </p>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#2F2F2E', margin: 0, whiteSpace: 'pre-wrap' }}>{h.texto}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function CRM() {
  const [cards, setCards]           = useState([])
  const [clientes, setClientes]     = useState([])
  const [loading, setLoading]       = useState(true)
  const [filtroTipo, setFiltroTipo] = useState('todos')
  const [filtroRamo, setFiltroRamo] = useState('todos')
  const [busca, setBusca]           = useState('')
  const [dragging, setDragging]     = useState(null)
  const [confirmarCadastroProposta, setConfirmarCadastroProposta] = useState(null) // card movido para "Fechado"
  const [perdaPendente, setPerdaPendente] = useState(null) // { card, viaFormulario } — card indo para "Perdido"
  const [perda, setPerda] = useState(PERDA_VAZIA)
  const [registrandoPerda, setRegistrandoPerda] = useState(false)
  const [confirmarExcluirCard, setConfirmarExcluirCard] = useState(false)
  const [formaCadastroProposta, setFormaCadastroProposta] = useState(false) // 2a etapa: manual ou PDF
  const [modalAberto, setModalAberto] = useState(false)
  // Historico de Atendimento (coluna direita) nao pode crescer mais que a coluna Dados (esquerda)
  // — mede a altura real dela (varia com Apólice Vinculada/Anexos) e usa como teto, com scroll
  // interno pra qualquer excedente, em vez de deixar o modal inteiro esticar sem limite.
  const colunaEsquerdaRef = useRef(null)
  const [alturaColunaEsquerda, setAlturaColunaEsquerda] = useState(null)
  const [form, setForm]             = useState(MODAL_VAZIO)
  const [editandoId, setEditandoId] = useState(null)
  const [buscaCliente, setBuscaCliente] = useState('') // texto digitado na busca de cliente (Nova Negociação)
  const [mostrarSugestoesCliente, setMostrarSugestoesCliente] = useState(false)
  const [salvando, setSalvando]     = useState(false)
  const [infoPDF, setInfoPDF]           = useState(null)
  const [gerandoExcel,  setGerandoExcel]  = useState(false)
  const [gerandoParc,   setGerandoParc]   = useState(false)
  const [gerandoParcPDF, setGerandoParcPDF] = useState(false)
  const [gerandoCotPDF, setGerandoCotPDF] = useState(false)
  const [modalOrcaCompacto, setModalOrcaCompacto] = useState(false)
  const [dropdownOrca, setDropdownOrca] = useState(false)
  const dropdownOrcaRef = useRef()
  const [orcaCompactoFile,  setOrcaCompactoFile]  = useState(null)
  const [modalCotPy,    setModalCotPy]    = useState(false)
  const [cotPyStep,     setCotPyStep]     = useState('pdfs') // 'pdfs' | 'files' | 'confirmar' | 'coberturas'
  const [cotPyCompacto, setCotPyCompacto] = useState(null)
  const [cotPyParc,     setCotPyParc]     = useState(null)
  const [cotPyPdfCompacto, setCotPyPdfCompacto] = useState(null)
  const [cotPyPdfParc,     setCotPyPdfParc]     = useState(null)
  const [processandoPdfs,  setProcessandoPdfs]  = useState(false)
  const [gerandoCotPy,  setGerandoCotPy]  = useState(false)
  const [cotPyNomes,    setCotPyNomes]    = useState([])
  const [cotPyCobs,     setCotPyCobs]     = useState([])
  const [cotPyParcPaginas, setCotPyParcPaginas] = useState([])
  const [cotPyDadosGerais, setCotPyDadosGerais] = useState({})
  const [cotPyFranquia, setCotPyFranquia] = useState([])
  // Cotação PDF disparada de dentro do card "Nova Negociação" (em vez do botão do cabeçalho do
  // CRM): ao gerar, alem de baixar o PDF, tenta casar/preencher cliente+ramo no form da
  // negociação e guarda o arquivo pra anexar automaticamente quando a negociação for salva
  // (o card ainda não existe no banco nesse momento, então não dá pra anexar direto).
  const [cotPyOrigemNegociacao, setCotPyOrigemNegociacao] = useState(false)
  const [cotacaoPdfPendente, setCotacaoPdfPendente] = useState(null)
  // Incrementado toda vez que um anexo é enviado "por fora" do componente FunilAnexos (Cotação
  // PDF gerada durante a edição), forçando a lista de anexos a recarregar.
  const [funilAnexosRefreshKey, setFunilAnexosRefreshKey] = useState(0)
  const inputExcelRef                     = useRef()
  const inputParcRef                      = useRef()
  const inputParcPDFRef                   = useRef()
  const inputCotPDFRef                    = useRef()
  const cotPyCompactoRef                  = useRef()
  const cotPyParcRef                      = useRef()
  const navigate                        = useNavigate()
  const [apoliceOrigem, setApoliceOrigem] = useState(null)
  // Cadeia da apolice vinculada: [apolice, endosso 1, endosso 2, ...] (premio somado + PDFs)
  const [cadeiaOrigem, setCadeiaOrigem] = useState([])
  const [pdfMenuOrigem, setPdfMenuOrigem] = useState(false)
  const pdfMenuOrigemRef = useRef(null)

  useEffect(() => { carregarDados() }, [])

  // Mede a altura da coluna Dados sempre que o modal esta aberto, pra travar a altura maxima da
  // coluna Histórico de Atendimento no mesmo valor (ResizeObserver reage a qualquer mudanca de
  // conteudo — Apólice Vinculada aparecendo/sumindo, Anexos, etc).
  useEffect(() => {
    if (!modalAberto || !colunaEsquerdaRef.current) return
    const el = colunaEsquerdaRef.current
    const observer = new ResizeObserver(entries => {
      setAlturaColunaEsquerda(entries[0].contentRect.height)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [modalAberto])

  // Card gerado por "Gerar Card Renov." (Renovacoes.jsx) ou "Gerar Card CRM" (Apolices.jsx) vem
  // com a apolice de origem vinculada — busca os dados dela pra mostrar na aba Dados (so quando o
  // Tipo nao for "Novo", ver bloco abaixo).
  useEffect(() => {
    setPdfMenuOrigem(false)
    if (!form.apolice_origem_id) { setApoliceOrigem(null); setCadeiaOrigem([]); return }
    api.get(`/apolices/${form.apolice_origem_id}`).then(r => setApoliceOrigem(r.data)).catch(() => setApoliceOrigem(null))
    // Cadeia de endossos (fila: cada endosso aponta pro anterior via documento_origem_id). O
    // vinculo pode ser a apolice ou um endosso dela: sobe ate a apolice e desce pela fila.
    api.get(`/apolices/${form.apolice_origem_id}/historico`).then(r => {
      const docs = Array.isArray(r.data) ? r.data : []
      const porId = new Map(docs.map(d => [String(d.id), d]))
      let raiz = porId.get(String(form.apolice_origem_id))
      const vistos = new Set()
      while (raiz?.eh_endosso && raiz.documento_origem_id && porId.has(String(raiz.documento_origem_id)) && !vistos.has(raiz.id)) {
        vistos.add(raiz.id)
        raiz = porId.get(String(raiz.documento_origem_id))
      }
      if (!raiz) { setCadeiaOrigem([]); return }
      const cadeia = [raiz]
      let x = raiz
      for (;;) {
        const prox = docs.find(d => d.eh_endosso && String(d.documento_origem_id) === String(x.id) && !cadeia.includes(d))
        if (!prox) break
        cadeia.push(prox)
        x = prox
      }
      setCadeiaOrigem(cadeia)
    }).catch(() => setCadeiaOrigem([]))
  }, [form.apolice_origem_id])

  useEffect(() => {
    if (!pdfMenuOrigem) return
    const handler = (e) => { if (pdfMenuOrigemRef.current && !pdfMenuOrigemRef.current.contains(e.target)) setPdfMenuOrigem(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [pdfMenuOrigem])

  // Com endosso, o premio da apolice vinculada e a soma da apolice + cada endosso (restituicao vem
  // negativa e desconta); endosso recusado/cancelado nao entra. Mesma regra da tela Renovacoes.
  const somaPremioOrigem = (campo) => {
    const docs = cadeiaOrigem.length ? cadeiaOrigem.filter(d => !d.eh_endosso || !['recusada', 'cancelada'].includes(d.status)) : (apoliceOrigem ? [apoliceOrigem] : [])
    return docs.reduce((s, d) => s + (parseFloat(campo === 'total' ? (d.premio_total || d.valor_premio) : d.premio_liquido) || 0), 0)
  }

  // PDF de um documento da cadeia: na apolice, o anexo mais recente do tipo "Apólice"; no endosso,
  // o do tipo/nome "Endosso" (senao o PDF mais recente dele). A aba e aberta antes do await pro
  // navegador nao bloquear como pop-up.
  const verPdfDocumento = async (doc) => {
    const aba = window.open('', '_blank')
    const rotulo = doc.eh_endosso ? 'endosso' : 'apólice'
    try {
      const { data: anexos } = await api.get(`/apolices/${doc.id}/anexos`)
      const pdfs = [...(Array.isArray(anexos) ? anexos : [])].filter(x => /\.pdf$/i.test(x.nome || '')).reverse()
      const re = doc.eh_endosso ? /endosso/i : /ap[oó]lice/i
      const anexo = pdfs.find(x => re.test(x.tipo || '')) || pdfs.find(x => re.test(x.nome || '')) || (doc.eh_endosso ? pdfs[0] : null)
      if (!anexo) { aba?.close(); avisar(`Nenhum PDF de ${rotulo} anexado a este documento.`); return }
      const { data } = await api.get(`/apolices/anexos/${anexo.id}/dados`)
      const [cab, base64] = data.dados.split(',')
      const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0))
      const url = URL.createObjectURL(new Blob([bytes], { type: cab.match(/data:(.*);base64/)?.[1] || 'application/pdf' }))
      if (aba) aba.location.href = url
      else window.open(url, '_blank')
    } catch (err) {
      aba?.close()
      avisar(`Erro ao abrir o PDF do ${rotulo === 'endosso' ? 'endosso' : 'apólice'}: ` + (err.response?.data?.erro || err.message))
    }
  }

  // Vindo do botao "Novo Negocio" da Home: abre direto o form de Nova Negociacao.
  useEffect(() => {
    if (sessionStorage.getItem('crmAbrirNovo')) {
      sessionStorage.removeItem('crmAbrirNovo')
      abrirNova()
    }
  }, [])
  useEffect(() => {
    const handler = (e) => { if (dropdownOrcaRef.current && !dropdownOrcaRef.current.contains(e.target)) setDropdownOrca(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const carregarClientes = async () => {
    try {
      const res = await api.get('/clientes')
      setClientes(res.data)
    } catch (err) {
      console.error('Erro ao carregar clientes:', err)
    }
  }

  const carregarDados = async () => {
    setLoading(true)
    try {
      const [resCards, resClientes] = await Promise.allSettled([
        api.get('/funil'),
        api.get('/clientes'),
      ])
      if (resCards.status === 'fulfilled') setCards(resCards.value.data)
      if (resClientes.status === 'fulfilled') setClientes(resClientes.value.data)

      // Restaurar modal pendente ANTES de desativar loading
      const pendente = sessionStorage.getItem('crmFormPendente')
      if (pendente) {
        sessionStorage.removeItem('crmFormPendente')
        const { editandoId: eid, ...formSalvo } = JSON.parse(pendente)
        setForm(formSalvo)
        setEditandoId(eid || null)
        setModalAberto(true)
      }
      // Vindo da etiqueta "CRM: ..." da tela Renovacoes: abre o card direto
      const abrirCardId = sessionStorage.getItem('crmAbrirCardId')
      if (abrirCardId) {
        sessionStorage.removeItem('crmAbrirCardId')
        const card = resCards.status === 'fulfilled' && resCards.value.data.find(c => String(c.id) === abrirCardId)
        if (card) abrirEditar(card)
      }
    } catch (err) {
      console.error('Erro ao carregar CRM:', err)
    } finally {
      setLoading(false)
    }
  }

  // Busca por nome, CPF/CNPJ, telefone ou observacoes (CPF/telefone casam com ou sem pontuacao)
  const q = busca.toLowerCase().trim()
  const qDigitos = q.replace(/\D/g, '')
  const casaBusca = (n) => {
    if (!q) return true
    if (n.nome?.toLowerCase().includes(q) || n.observacoes?.toLowerCase().includes(q)) return true
    if (!qDigitos) return false
    let tels = []
    try { const t = typeof n.telefones === 'string' ? JSON.parse(n.telefones) : n.telefones; tels = Array.isArray(t) ? t.map(x => x?.numero || '') : [] } catch {}
    return [n.cpf_cnpj || '', ...tels].some(v => String(v).replace(/\D/g, '').includes(qDigitos))
  }
  const filtrados = cards.filter(n => {
    if (filtroTipo !== 'todos' && n.tipo !== filtroTipo) return false
    if (filtroRamo !== 'todos' && n.ramo !== filtroRamo) return false
    return casaBusca(n)
  })

  const porColuna = (id) => filtrados.filter(n => n.etapa === id)

  // Drag and drop
  const onDragStart = (e, card) => { setDragging(card); e.dataTransfer.effectAllowed = 'move' }
  const onDragOver  = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }
  const onDrop = async (e, etapa) => {
    e.preventDefault()
    if (!dragging || dragging.etapa === etapa) return
    const card = dragging
    // "Perdido": so move depois de responder o motivo (Cancelar deixa o card onde estava)
    if (etapa === 'fechamento') {
      setDragging(null)
      setPerda(PERDA_VAZIA)
      setPerdaPendente({ card, viaFormulario: false })
      return
    }
    setCards(prev => prev.map(c => c.id === card.id ? { ...c, etapa } : c))
    setDragging(null)
    try {
      await api.patch(`/funil/${card.id}/etapa`, { etapa })
      // Ao mover para "Fechado" (negociacao), pergunta se ja quer cadastrar a Proposta agora.
      if (etapa === 'negociacao') setConfirmarCadastroProposta(card)
    } catch (err) {
      carregarDados() // reverter se falhar
    }
  }

  const cadastrarPropostaManualmente = () => {
    sessionStorage.setItem('abrirNovaPropostaClienteId', String(confirmarCadastroProposta.cliente_id))
    // Card de renovacao/refeito: leva a apolice de origem pra proposta nascer ja vinculada
    // (na importacao por PDF o servidor acha o card sozinho — ver vincularPorCardCrm).
    if (confirmarCadastroProposta.apolice_origem_id && ['Renovação Interna', 'Refeito'].includes(confirmarCadastroProposta.tipo)) {
      sessionStorage.setItem('abrirNovaPropostaOrigem', JSON.stringify({ id: confirmarCadastroProposta.apolice_origem_id, motivo: confirmarCadastroProposta.tipo }))
    }
    setConfirmarCadastroProposta(null)
    setFormaCadastroProposta(false)
    navigate('/propostas')
  }

  const cadastrarPropostaViaPDF = () => {
    sessionStorage.setItem('abrirImportarPropostaAoCarregar', '1')
    setConfirmarCadastroProposta(null)
    setFormaCadastroProposta(false)
    navigate('/propostas')
  }

  const lembrarDepois = () => {
    avisar(`Não esqueça de cadastrar a proposta de ${confirmarCadastroProposta.nome} depois!`)
    setConfirmarCadastroProposta(null)
    setFormaCadastroProposta(false)
  }

  // Modal
  const abrirNova = (etapa = 'prospeccao') => {
    setForm({ ...MODAL_VAZIO, etapa })
    setEditandoId(null)
    setInfoPDF(null)
    setCotacaoPdfPendente(null)
    setBuscaCliente('')
    carregarClientes()
    setModalAberto(true)
  }
  const abrirEditar = (card) => {
    setForm({ cliente_id: card.cliente_id, etapa: card.etapa, ramo: card.ramo || 'Auto', tipo: card.tipo || 'Novo', observacoes: card.observacoes || '', apolice_origem_id: card.apolice_origem_id || null })
    setEditandoId(card.id)
    setModalAberto(true)
  }
  const fecharModal = () => { setModalAberto(false); setEditandoId(null); setInfoPDF(null); setAlturaColunaEsquerda(null); setCotacaoPdfPendente(null) }

  // Leva pra pagina do cliente (consulta/edicao) e lembra de voltar pro card aberto no CRM.
  const irParaCliente = () => {
    sessionStorage.setItem('abrirClienteId', form.cliente_id)
    sessionStorage.setItem('voltarPara', '/crm')
    sessionStorage.setItem('crmFormPendente', JSON.stringify({ ...form, editandoId }))
    navigate('/clientes')
  }

  // Leva pra pagina da apolice de origem vinculada ao card e lembra de voltar pro card aberto no
  // CRM. Mesmo mecanismo abrirApoliceId/voltarPara ja usado por Renovacoes.jsx <-> Apolices.jsx.
  const irParaApolice = () => {
    sessionStorage.setItem('abrirApoliceId', String(form.apolice_origem_id))
    sessionStorage.setItem('voltarApoliceLabel', 'CRM')
    sessionStorage.setItem('voltarPara', '/crm')
    sessionStorage.setItem('crmFormPendente', JSON.stringify({ ...form, editandoId }))
    navigate('/apolices')
  }

  const salvar = async (perdaJaRegistrada = false) => {
    if (!form.cliente_id) return avisar('Selecione um cliente')
    // Etapa trocada para "Perdido" pela tela de edicao: pergunta o motivo antes de salvar
    const etapaAnterior = editandoId ? cards.find(c => c.id === editandoId)?.etapa : null
    if (!perdaJaRegistrada && editandoId && form.etapa === 'fechamento' && etapaAnterior !== 'fechamento') {
      setPerda(PERDA_VAZIA)
      setPerdaPendente({ card: cards.find(c => c.id === editandoId), viaFormulario: true })
      return
    }
    setSalvando(true)
    try {
      if (editandoId) {
        const { data } = await api.put(`/funil/${editandoId}`, form)
        setCards(prev => prev.map(c => c.id === editandoId ? data : c))
        fecharModal()
      } else {
        const { data } = await api.post('/funil', form)
        setCards(prev => [data, ...prev])
        // Cotação PDF gerada durante a criação (card ainda não existia pra anexar antes) —
        // anexa agora que já temos o id do card recém-criado.
        if (cotacaoPdfPendente) {
          try {
            const dadosUrl = await blobParaDataUrl(cotacaoPdfPendente.blob)
            await api.post(`/funil/${data.id}/anexos`, {
              nome: cotacaoPdfPendente.nome, tamanho: cotacaoPdfPendente.blob.size, dados: dadosUrl,
            })
          } catch (err) {
            avisar('Negociação salva, mas houve erro ao anexar a Cotação PDF: ' + (err.response?.data?.erro || err.message))
          }
          setCotacaoPdfPendente(null)
        }
        // Em vez de fechar, permanece na tela já em modo edição — o card acabou de ser criado,
        // então Anexos/Histórico de Atendimento passam a ficar disponíveis na mesma tela.
        setForm({ cliente_id: data.cliente_id, etapa: data.etapa, ramo: data.ramo || 'Automóvel', tipo: data.tipo || 'Novo', observacoes: data.observacoes || '', apolice_origem_id: data.apolice_origem_id || null })
        setInfoPDF(null)
        setEditandoId(data.id)
      }
    } catch (err) {
      avisar('Erro ao salvar: ' + (err.response?.data?.erro || err.message))
    } finally {
      setSalvando(false)
    }
  }

  // Grava o motivo no Historico de Atendimento e nos campos da tela Perdidos, e move o card pra
  // "Perdido" (arrastando) ou segue o salvar da tela de edicao.
  const confirmarPerda = async () => {
    const { card, viaFormulario } = perdaPendente
    setRegistrandoPerda(true)
    try {
      const retorno = perda.retornar === 'sim' ? perda.dataRetorno : null
      const texto = [
        `Negociação perdida. Motivo: ${perda.motivo}.`,
        perda.obs.trim() ? `Obs.: ${perda.obs.trim()}` : '',
        `Retornar contato no próximo ano: ${retorno ? `Sim — retorno em ${fmtDataBR(retorno)}` : 'Não'}`,
      ].filter(Boolean).join('\n')
      await api.post(`/funil/${card.id}/historico`, { texto })
      // Campos proprios (tela Perdidos lista os que tem "retornar contato")
      await api.patch(`/funil/${card.id}/perda`, {
        motivo_perda: perda.motivo, obs_perda: perda.obs.trim() || null,
        retornar_contato: perda.retornar === 'sim', data_retorno: retorno,
      })
      setPerdaPendente(null)
      if (viaFormulario) {
        await salvar(true)
      } else {
        setCards(prev => prev.map(c => c.id === card.id ? { ...c, etapa: 'fechamento' } : c))
        await api.patch(`/funil/${card.id}/etapa`, { etapa: 'fechamento' })
      }
      avisar(retorno ? `Card movido para Perdido. Retorno de contato em ${fmtDataBR(retorno)} (tela Perdidos).` : 'Card movido para Perdido.', 'sucesso')
    } catch (err) {
      avisar('Erro ao registrar a perda: ' + (err.response?.data?.erro || err.message), 'erro')
      carregarDados()
    } finally {
      setRegistrandoPerda(false)
    }
  }

  const excluir = () => setConfirmarExcluirCard(true)
  const confirmarExclusaoCard = async () => {
    setConfirmarExcluirCard(false)
    try {
      await api.delete(`/funil/${editandoId}`)
      setCards(prev => prev.filter(c => c.id !== editandoId))
      fecharModal()
    } catch (err) {
      avisar('Erro ao excluir')
    }
  }

  const criarClientePDF = async () => {
    if (!infoPDF?.dadosParaCriar) return
    setSalvando(true)
    try {
      const d = infoPDF.dadosParaCriar
      const { data: novoCliente } = await api.post('/clientes', {
        nome: d.segurado,
        cpf_cnpj: d.cpf,
        cep: d.cep,
        sexo: d.sexo,
        estado_civil: d.estado_civil,
        data_nascimento: d.data_nascimento || null,
        telefones: [],
        emails: [],
      })
      setClientes(prev => [novoCliente, ...prev])
      setForm(f => ({ ...f, cliente_id: String(novoCliente.id) }))
      setInfoPDF(prev => ({ ...prev, clienteEncontrado: novoCliente.nome, dadosParaCriar: null }))
    } catch (err) {
      avisar('Erro ao criar cliente: ' + (err.response?.data?.erro || err.message))
    } finally {
      setSalvando(false)
    }
  }

  const exportarCotacaoExcel = async (compactoFile) => {
    if (!compactoFile) return
    setGerandoExcel(true)
    setModalOrcaCompacto(false)
    try {
      const items = await extrairItemsPosicao(compactoFile)
      const dados = parsearOrcaCompacto(items)
      let nomesVision = []

      // Tenta identificar seguradoras pelo logo via backend
      try {
        const mesclarUrl = process.env.REACT_APP_MESCLAR_PDF_URL || 'https://corretora-mesclar-pdf.onrender.com'
        const fd = new FormData()
        fd.append('compacto', compactoFile)
        const res = await fetch(`${mesclarUrl}/identificar-seguradoras`, { method: 'POST', body: fd })
        if (res.ok) {
          const nomes = await res.json() // array de nomes por coluna, ex: ['Porto Seguro', 'HDI', '']
          console.log('[identificar-seguradoras] nomes retornados:', nomes)
          console.log('[identificar-seguradoras] header coberturas row 0:', dados.tabelaCoberturas[0])
          if (nomes.some(n => n)) {
            // Substitui apenas células que são número de cotação "Nº XXXXXXX" pelo nome da seguradora
            const isNumCotacao = v => /N[°º]\s*\d{5,}/.test(String(v || ''))
            for (const tabela of [dados.tabelaCoberturas, dados.tabelaFranquia]) {
              for (const row of tabela) {
                for (let i = 1; i < row.length; i++) {
                  if (isNumCotacao(row[i]) && nomes[i - 1]) row[i] = nomes[i - 1]
                }
              }
            }
          }
        }
      } catch (_) { /* fallback: mantém números */ }

      gerarExcelCotacao(dados, compactoFile.name.replace(/\.pdf$/i, '.xlsx'))
    } catch (err) {
      avisar('Erro ao gerar Excel: ' + err.message)
    } finally {
      setGerandoExcel(false)
      setOrcaCompactoFile(null)
    }
  }

  const exportarParcelamentoExcel = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setGerandoParc(true)
    try {
      const paginas = await parsearParcelamentoPDF(file)
      if (!paginas.length) throw new Error('Nenhuma tabela de parcelamento encontrada')
      gerarExcelParcelamento(paginas, file.name.replace(/\.pdf$/i, '.xlsx'))
    } catch (err) {
      avisar('Erro ao gerar Excel: ' + err.message)
    } finally {
      setGerandoParc(false)
      e.target.value = ''
    }
  }

  const exportarParcelamentoPDF = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setGerandoParcPDF(true)
    try {
      const paginas = await parsearParcelamentoPDF(file)
      if (!paginas.length) throw new Error('Nenhuma tabela de parcelamento encontrada')
      gerarPDFParcelamento(paginas)
    } catch (err) {
      avisar('Erro ao gerar PDF: ' + err.message)
    } finally {
      setGerandoParcPDF(false)
      e.target.value = ''
    }
  }

  const exportarCotacaoExcelParaPDF = async (e) => {
    const file = e.target.files[0]
    e.target.value = ''
    if (!file) return
    setGerandoCotPDF(true)
    try {
      const buffer = await file.arrayBuffer()
      const wb = XLSX.read(buffer, { type: 'array' })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })

      // ── Parsing ────────────────────────────────────────────────────────────
      let mode = ''
      let franquiaFound = false
      const simpleFields = []   // [{ type:'section'|'field', label, val? }]
      let seg1 = '', seg2 = '', num1 = '', num2 = ''
      const cobRows = []        // [{ label, val1, val2 }]
      const franqRows = []      // [{ label, val1, val2 }]

      const SIMPLE_SECTIONS = ['DADOS DA COTAÇÃO', 'PERFIL DO CONDUTOR', 'DADOS DO VEÍCULO']

      for (const row of rows) {
        const a = String(row[0] ?? '').trim()
        const b = String(row[1] ?? '').trim()
        const c = String(row[2] ?? '').trim()

        // Seções simples
        if (SIMPLE_SECTIONS.includes(a)) {
          simpleFields.push({ type: 'section', label: a })
          mode = 'simple'
          continue
        }

        // Seção COBERTURAS — entra no modo na 1ª ocorrência; 2ª captura nomes de seguradora
        if (a === 'COBERTURAS') {
          if (mode !== 'coberturas') {
            // Primeira linha "COBERTURAS": header da seção
            seg1 = b; seg2 = c   // pode estar vazio; será sobrescrito pela 2ª linha se necessário
            mode = 'coberturas'
          } else if (!seg1 && b) {
            // Segunda linha "COBERTURAS": nomes de seguradora (ex: "Porto Seguro" | "HDI")
            seg1 = b; seg2 = c
          }
          continue
        }

        // Seção FRANQUIA
        if (a === 'FRANQUIA' && !b && !franquiaFound) {
          franquiaFound = true
          mode = 'franquia_wait'
          continue
        }
        if (mode === 'franquia_wait') {
          franqRows.push({ label: a || 'FRANQUIA', val1: b, val2: c })
          mode = 'franquia'
          continue
        }

        // Linhas de dados
        if (mode === 'simple' && a && b) {
          simpleFields.push({ type: 'field', label: a, val: b })
        } else if (mode === 'coberturas') {
          if (!a && b) {
            // Linha de números de cotação (ex: "Nº 1259255670")
            num1 = b; num2 = c
          } else if (a && (b || c)) {
            cobRows.push({ label: a, val1: b, val2: c })
          }
        } else if (mode === 'franquia' && a && (b || c)) {
          franqRows.push({ label: a, val1: b, val2: c })
        }
      }

      // ── Helpers HTML ───────────────────────────────────────────────────────
      const C = {
        blue: 'var(--primary)', lightBlue: 'var(--primary-light)', border: '#FFD5C8',
        text: '#1A1A2E', label: '#555', rowEven: '#F5F8FC',
      }

      const buildSimpleTable = () => {
        if (!simpleFields.length) return ''
        let rows = ''
        simpleFields.forEach(f => {
          if (f.type === 'section') {
            rows += `<tr><td colspan="2" style="background:${C.lightBlue};color:${C.blue};font-weight:700;font-size:11.5px;padding:7px 12px;border-bottom:2px solid ${C.blue};border-top:8px solid #fff;">${escapeHtml(f.label)}</td></tr>`
          } else {
            rows += `<tr><td style="padding:5px 12px;font-size:11px;color:${C.label};font-weight:600;width:38%;border-bottom:1px solid ${C.border};">${escapeHtml(f.label)}</td><td style="padding:5px 12px;font-size:11px;color:${C.text};border-bottom:1px solid ${C.border};">${escapeHtml(f.val)}</td></tr>`
          }
        })
        return `<table style="width:100%;border-collapse:collapse;margin-bottom:12px;"><tbody>${rows}</tbody></table>`
      }

      // Detecta dual-coluna pela presença de dados em C, não só pelo nome da seguradora
      const hasDualCol = seg2 !== '' || cobRows.some(r => r.val2 !== '') || franqRows.some(r => r.val2 !== '')
      const buildCobTable = () => {
        if (!cobRows.length && !franqRows.length) return ''
        const colCount = hasDualCol ? 3 : 2

        const buildHeader = (title) => `<tr><td colspan="${colCount}" style="background:${C.lightBlue};color:${C.blue};font-weight:700;font-size:11.5px;padding:7px 12px;border-bottom:2px solid ${C.blue};border-top:8px solid #fff;">${title}</td></tr>`

        const seg1LabelRaw = seg1 || num1 || 'Seguradora 1'
        const seg2LabelRaw = seg2 || num2 || 'Seguradora 2'
        const seg1Hdr = escapeHtml(seg1LabelRaw) + (num1 && num1 !== seg1LabelRaw ? `<br><span style="font-weight:400;font-size:10px;">${escapeHtml(num1)}</span>` : '')
        const seg2Hdr = escapeHtml(seg2LabelRaw) + (num2 && num2 !== seg2LabelRaw ? `<br><span style="font-weight:400;font-size:10px;">${escapeHtml(num2)}</span>` : '')

        let cobBody = hasDualCol
          ? `<tr style="background:${C.blue}"><td style="padding:6px 12px;font-size:11px;color:#fff;font-weight:700;width:35%;">Cobertura</td><td style="padding:6px 12px;font-size:11px;color:#fff;font-weight:700;text-align:center;">${seg1Hdr}</td><td style="padding:6px 12px;font-size:11px;color:#fff;font-weight:700;text-align:center;">${seg2Hdr}</td></tr>`
          : `<tr style="background:${C.blue}"><td style="padding:6px 12px;font-size:11px;color:#fff;font-weight:700;width:40%;">Cobertura</td><td style="padding:6px 12px;font-size:11px;color:#fff;font-weight:700;text-align:center;">${seg1Hdr}</td></tr>`

        cobRows.forEach((r, i) => {
          const bg = i % 2 === 0 ? '#fff' : C.rowEven
          cobBody += hasDualCol
            ? `<tr style="background:${bg};border-bottom:1px solid ${C.border}"><td style="padding:5px 12px;font-size:11px;color:${C.label};font-weight:600;">${escapeHtml(r.label)}</td><td style="padding:5px 12px;font-size:11px;color:${C.text};text-align:center;">${escapeHtml(r.val1)}</td><td style="padding:5px 12px;font-size:11px;color:${C.text};text-align:center;">${escapeHtml(r.val2)}</td></tr>`
            : `<tr style="background:${bg};border-bottom:1px solid ${C.border}"><td style="padding:5px 12px;font-size:11px;color:${C.label};font-weight:600;">${escapeHtml(r.label)}</td><td style="padding:5px 12px;font-size:11px;color:${C.text};text-align:center;">${escapeHtml(r.val1)}</td></tr>`
        })

        let franqBody = ''
        franqRows.forEach((r, i) => {
          const bg = i % 2 === 0 ? '#fff' : C.rowEven
          franqBody += hasDualCol
            ? `<tr style="background:${bg};border-bottom:1px solid ${C.border}"><td style="padding:5px 12px;font-size:11px;color:${C.label};font-weight:600;">${escapeHtml(r.label)}</td><td style="padding:5px 12px;font-size:11px;color:${C.text};text-align:center;">${escapeHtml(r.val1)}</td><td style="padding:5px 12px;font-size:11px;color:${C.text};text-align:center;">${escapeHtml(r.val2)}</td></tr>`
            : `<tr style="background:${bg};border-bottom:1px solid ${C.border}"><td style="padding:5px 12px;font-size:11px;color:${C.label};font-weight:600;">${escapeHtml(r.label)}</td><td style="padding:5px 12px;font-size:11px;color:${C.text};text-align:center;">${escapeHtml(r.val1)}</td></tr>`
        })

        return `<table style="width:100%;border-collapse:collapse;margin-bottom:12px;"><tbody>
          ${buildHeader('COBERTURAS')}${cobBody}
          ${franqRows.length ? buildHeader('FRANQUIA') + franqBody : ''}
        </tbody></table>`
      }

      const hoje = new Date().toLocaleDateString('pt-BR')
      const html = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<title>Cotação</title>
<style>
  body{font-family:Arial,sans-serif;margin:0;padding:0;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
  .page{max-width:780px;margin:0 auto;padding:20px;}
  .hdr{background:${C.blue};color:#fff;padding:16px 22px;border-radius:6px;margin-bottom:18px;display:flex;justify-content:space-between;align-items:center;}
  .hdr h1{margin:0;font-size:17px;font-weight:700;letter-spacing:.5px;}
  .hdr .sub{font-size:11px;opacity:.85;margin-top:4px;}
  .footer{text-align:center;font-size:10px;color:#aaa;margin-top:20px;border-top:1px solid #eee;padding-top:10px;}
  @media print{@page{size:A4;margin:12mm;}.page{padding:0;}}
</style>
</head>
<body>
<div class="page">
  <div class="hdr">
    <div>
      <h1>COTAÇÃO DE SEGURO</h1>
      <div class="sub">Gerado em ${hoje}</div>
    </div>
    <div style="font-size:11px;opacity:.85;">Sistema Corretora</div>
  </div>
  ${buildSimpleTable()}
  ${buildCobTable()}
  <div class="footer">Documento gerado pelo sistema Corretora &mdash; ${hoje}</div>
</div>
<script>setTimeout(()=>window.print(),400)</script>
</body></html>`

      const win = window.open('', '_blank')
      if (win) { win.document.write(html); win.document.close() }
      else avisar('Permita pop-ups para gerar o PDF.')
    } catch (err) {
      avisar('Erro ao processar Excel: ' + err.message)
    } finally {
      setGerandoCotPDF(false)
    }
  }

  const fecharModalCotPy = () => {
    if (cotPyCompactoRef.current) cotPyCompactoRef.current.value = ''
    if (cotPyParcRef.current)     cotPyParcRef.current.value = ''
    setModalCotPy(false)
    setCotPyStep('pdfs')
    setCotPyCompacto(null)
    setCotPyParc(null)
    setCotPyPdfCompacto(null)
    setCotPyPdfParc(null)
    setCotPyNomes([])
    setCotPyCobs([])
    setCotPyParcPaginas([])
    setCotPyDadosGerais({})
    setCotPyFranquia([])
    setCotPyOrigemNegociacao(false)
  }

  const gerarFluxoDePDFs = async () => {
    if (!cotPyPdfCompacto || !cotPyPdfParc) return
    setProcessandoPdfs(true)
    try {
      // 1. Gera Excel Compacto em memória
      const items = await extrairItemsPosicao(cotPyPdfCompacto)
      const dados = parsearOrcaCompacto(items)
      let nomesVision = []
      try {
        const mesclarUrl = process.env.REACT_APP_MESCLAR_PDF_URL || 'https://corretora-mesclar-pdf.onrender.com'
        const fd = new FormData()
        fd.append('compacto', cotPyPdfCompacto)
        const res = await fetch(`${mesclarUrl}/identificar-seguradoras`, { method: 'POST', body: fd })
        if (res.ok) {
          nomesVision = await res.json()
          
        }
      } catch (_) {}
      const compactoFile = gerarExcelCotacaoEmMemoria(dados)

      // 2. Gera Excel Parcelamento em memória
      const paginas = await parsearParcelamentoPDF(cotPyPdfParc)
      if (!paginas.length) throw new Error('Nenhuma tabela de parcelamento encontrada')
      const parcFile = gerarExcelParcelamentoEmMemoria(paginas)

      // 3. Seta os arquivos e avança para confirmar (inline avancarParaConfirmar com vars locais)
      setCotPyCompacto(compactoFile)
      setCotPyParc(parcFile)
      const { nomes: nomesExcel, rows, dadosGerais, franquia } = await parsearCoberturasExcel(compactoFile)
      setCotPyDadosGerais(dadosGerais)
      setCotPyFranquia(franquia)
      let nomesFinais = nomesExcel
      try {
        const buf2 = await parcFile.arrayBuffer()
        const wb2 = XLSX.read(buf2, { type: 'array' })
        const ws2 = wb2.Sheets[wb2.SheetNames[0]]
        const aoa2 = XLSX.utils.sheet_to_json(ws2, { header: 1, defval: '' })
        const mapaNomes = {}
        for (const row of aoa2) {
          const cell = String(row[0] || '')
          const m = cell.match(/N[oº°O]\s*(\d+)/)
          if (m) {
            const nome = cell.replace(/\s*N[oº°O]\s*\d+.*/, '').trim()
            if (nome) mapaNomes[m[1]] = nome
          }
        }
        nomesFinais = nomesExcel.map((n, idx) => {
          const d = n.replace(/\D/g, '')
          return resolverNomeFinalSeguradora({
            nomeCompacto: n,
            nomeVision: nomesVision[idx] || '',
            nomeParcelamento: mapaNomes[d] || '',
          })
        })
      } catch (_) {}
      setCotPyNomes(nomesFinais)
      setCotPyCobs(rows)
      const normParc = r => ({ parcelamento: r.label !== undefined ? r.label : (r.parcelamento || ''), total: r.total || '' })
      setCotPyParcPaginas(remapearPaginasParcelamentoPorNumero(paginas, nomesExcel, nomesFinais).map(pg => ({
        seguradora: pg.seguradora || '',
        numero: pg.numero || '',
        preco: pg.preco || '',
        franquiaValor: pg.franquiaValor || '',
        franquiaTipo: pg.franquiaTipo || '',
        debito:  (pg.debito  || []).map(normParc),
        credito: (pg.credito || []).map(normParc),
        boleto:  (pg.boleto  || []).map(normParc),
        observacoes: pg.observacoes || '',
      })))
      setCotPyStep('confirmar')
    } catch (err) {
      avisar('Erro ao processar PDFs: ' + err.message)
    } finally {
      setProcessandoPdfs(false)
    }
  }

  // Le o Excel Compacto inteiro numa unica passada: tabela de Coberturas (por seguradora) +
  // Dados Gerais (Dados da Cotação/Perfil do Condutor/Dados do Veículo) + Franquia (tipo/valor
  // por seguradora) — as 3 telas de edição do fluxo "Cotação PDF", espelhando as mesmas seções
  // que o backend (mesclar-pdf/cotacao_emseg.py, _parse_compacto) le do mesmo arquivo.
  const MAPA_DADOS_GERAIS = {
    'DADOS DA COTAÇÃO':   { 'Segurado': 'segurado', 'CPF/CNPJ': 'cpf', 'CEP': 'cep', 'Tipo Seguro': 'tipo_seguro', 'Vigência': 'vigencia' },
    'PERFIL DO CONDUTOR': { 'Principal Condutor': 'condutor', 'CPF': 'cpf_cond', 'Nascimento': 'nascimento', 'Sexo': 'sexo', 'Estado Civil': 'estado_civil', 'Tipo de Uso': 'uso', 'Reside 18-25 Anos': 'jovem_cond' },
    'DADOS DO VEÍCULO':   { 'Veículo': 'veiculo', 'Fabricação/Modelo': 'ano_modelo', 'Placa': 'placa', 'Chassi': 'chassi', 'FIPE': 'fipe', 'CEP Pernoite': 'cep_pernoite' },
  }
  const SECOES_COMPACTO = new Set(['DADOS DA COTAÇÃO', 'PERFIL DO CONDUTOR', 'DADOS DO VEÍCULO', 'COBERTURAS', 'FRANQUIA'])

  const parsearCoberturasExcel = async (file) => {
    const buf = await file.arrayBuffer()
    const wb = XLSX.read(buf, { type: 'array' })
    const ws = wb.Sheets[wb.SheetNames[0]]
    const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })
    let secao = null
    const nomes = []
    const rows = []
    const dadosGerais = {}
    const franTipoRaw = []
    const franValRaw = []
    for (const row of aoa) {
      const k = String(row[0] || '')
      const r1 = String(row[1] || '')
      if (SECOES_COMPACTO.has(k) && r1 === '') { secao = k; continue }
      if (!k && r1 === '') continue

      const mapaSecao = MAPA_DADOS_GERAIS[secao]
      if (mapaSecao && mapaSecao[k] && r1) { dadosGerais[mapaSecao[k]] = r1; continue }

      if (secao === 'COBERTURAS') {
        // Linha de nomes: col 0 vazia OU 'COBERTURAS' com col 1+ preenchida (formatos Nº e nome direto)
        const isNomesRow = r1 !== '' && nomes.length === 0 && (k === '' || k === 'COBERTURAS')
        if (r1.startsWith('Nº') || isNomesRow) {
          for (let i = 1; i < row.length; i++) if (row[i]) nomes.push(String(row[i]))
          continue
        }
        if (k && k !== 'COBERTURAS') {
          const n = Math.max(nomes.length, 1)
          rows.push({ label: k, values: Array.from({ length: n }, (_, i) => String(row[i + 1] || '')) })
        }
      } else if (secao === 'FRANQUIA') {
        if (k === 'FRANQUIA' && r1) { for (let i = 1; i < row.length; i++) if (row[i]) franTipoRaw.push(String(row[i])) }
        else if (k === 'Veículo' && r1) { for (let i = 1; i < row.length; i++) if (row[i]) franValRaw.push(String(row[i])) }
      }
    }
    const franquia = Array.from({ length: Math.max(franTipoRaw.length, franValRaw.length) }, (_, i) => ({
      tipo: franTipoRaw[i] || '', valor: franValRaw[i] || '',
    }))
    return { nomes, rows, dadosGerais, franquia }
  }

  const remapearPaginasParcelamentoPorNumero = (paginas, nomesCompacto, nomesFinais) => {
    if (!Array.isArray(paginas) || !paginas.length) return paginas
    const mapaPorNumero = {}
    nomesCompacto.forEach((nomeOriginal, idx) => {
      const numero = String(nomeOriginal || '').replace(/\D/g, '')
      const nomeFinal = nomesFinais[idx] || nomeOriginal
      if (numero && nomeFinal) mapaPorNumero[numero] = nomeFinal
    })
    return paginas.map(pg => {
      const numero = String(pg?.numero || '').replace(/\D/g, '')
      return numero && mapaPorNumero[numero]
        ? { ...pg, seguradora: mapaPorNumero[numero] }
        : pg
    })
  }

  const isNumeroCotacaoHeader = valor => /N[°º]\s*\d{5,}/.test(String(valor || ''))

  const resolverNomeFinalSeguradora = ({ nomeCompacto = '', nomeVision = '', nomeParcelamento = '' }) => {
    if (nomeVision) return nomeVision
    if (nomeCompacto && !isNumeroCotacaoHeader(nomeCompacto)) return nomeCompacto
    if (nomeParcelamento) return nomeParcelamento
    return nomeCompacto || nomeVision || nomeParcelamento || ''
  }

  const avancarParaConfirmar = async () => {
    if (!cotPyCompacto || !cotPyParc) return
    try {
      const { nomes, rows, dadosGerais, franquia } = await parsearCoberturasExcel(cotPyCompacto)
      setCotPyDadosGerais(dadosGerais)
      setCotPyFranquia(franquia)

      // Lê o parcelamento para substituir números pelos nomes reais das seguradoras
      // Ex: "Nº 1157622067" → "Porto Seguro"
      let nomesFinais = nomes
      try {
        const buf2 = await cotPyParc.arrayBuffer()
        const wb2 = XLSX.read(buf2, { type: 'array' })
        const ws2 = wb2.Sheets[wb2.SheetNames[0]]
        const aoa2 = XLSX.utils.sheet_to_json(ws2, { header: 1, defval: '' })
        const mapaNomes = {}
        for (const row of aoa2) {
          const cell = String(row[0] || '')
          const m = cell.match(/N[oº°O]\s*(\d+)/)
          if (m) {
            const nome = cell.replace(/\s*N[oº°O]\s*\d+.*/, '').trim()
            if (nome) mapaNomes[m[1]] = nome
          }
        }
        nomesFinais = nomes.map(n => {
          const digits = n.replace(/\D/g, '')
          return resolverNomeFinalSeguradora({
            nomeCompacto: n,
            nomeParcelamento: mapaNomes[digits] || '',
          })
        })
      } catch (_) { /* se falhar, usa números mesmo */ }

      setCotPyNomes(nomesFinais)
      setCotPyCobs(rows)
      try {
        const parcPaginas = await parsearParcExcel(cotPyParc)
        setCotPyParcPaginas(remapearPaginasParcelamentoPorNumero(parcPaginas, nomes, nomesFinais))
      } catch (_) {}
      setCotPyStep('confirmar')
    } catch (err) {
      avisar('Erro ao ler Excel: ' + err.message)
    }
  }

  const atualizarCobertura = (rowIdx, colIdx, valor) => {
    setCotPyCobs(prev => prev.map((r, i) => {
      if (i !== rowIdx) return r
      const values = [...r.values]
      values[colIdx] = valor
      return { ...r, values }
    }))
  }

  const atualizarDadoGeral = (chave, valor) => {
    setCotPyDadosGerais(prev => ({ ...prev, [chave]: valor }))
  }

  const atualizarFranquia = (idx, campo, valor) => {
    setCotPyFranquia(prev => prev.map((f, i) => i === idx ? { ...f, [campo]: valor } : f))
  }

  const parsearParcExcel = async (file) => {
    const buf = await file.arrayBuffer()
    const wb = XLSX.read(buf, { type: 'array' })
    const ws = wb.Sheets[wb.SheetNames[0]]
    const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })
    const paginas = []
    let current = null
    let inData = false
    for (const row of aoa) {
      const vals = row.map(v => String(v || '').trim())
      const c0 = vals[0]
      // Empty row resets data mode
      if (vals.every(v => !v)) { inData = false; continue }
      // Skip DÉBITO/CRÉDITO/BOLETO header row
      if (c0 === 'DÉBITO') continue
      // Parcelamento/Total header row — start collecting data
      if (c0 === 'Parcelamento') { inData = true; continue }
      // Data rows (inside a seguradora block)
      if (inData) {
        if (c0 === 'Obs:') { if (current) current.observacoes = vals[1] || ''; continue }
        if (vals[0] || vals[1]) current.debito.push({ parcelamento: vals[0], total: vals[1] })
        if (vals[2] || vals[3]) current.credito.push({ parcelamento: vals[2], total: vals[3] })
        if (vals[4] || vals[5]) current.boleto.push({ parcelamento: vals[4], total: vals[5] })
        continue
      }
      // Not in data mode and col0 has a name = seguradora header row
      // (the row may have Preço/Franquia in other cols — we only use col0)
      if (c0) {
        const numeroMatch = c0.match(/N[oº°O]\s*(\d+(?:-\d+)?)/)
        const seguradora = c0.replace(/\s*N[oº°O]\s*\d+(?:-\d+)?.*/, '').trim() || c0
        const preco = (vals[3] || '').replace(/^Preço:\s*/i, '').trim()
        const franquiaRaw = (vals[5] || '').replace(/^Franquia:\s*/i, '').trim()
        const franquiaValorMatch = franquiaRaw.match(/R\$\s*[\d.,]+/)
        const franquiaValor = franquiaValorMatch ? franquiaValorMatch[0] : ''
        const franquiaTipo = franquiaRaw.replace(/R\$\s*[\d.,]+/, '').trim()
        current = {
          seguradora,
          numero: numeroMatch ? numeroMatch[1] : '',
          preco,
          franquiaValor,
          franquiaTipo,
          debito: [],
          credito: [],
          boleto: [],
          observacoes: '',
        }
        paginas.push(current)
      }
    }
    return paginas
  }

  const atualizarParcelamento = (pgIdx, tipo, rowIdx, campo, valor) => {
    setCotPyParcPaginas(prev => prev.map((pg, i) => {
      if (i !== pgIdx) return pg
      const arr = [...pg[tipo]]
      while (arr.length <= rowIdx) arr.push({ parcelamento: '', total: '' })
      arr[rowIdx] = { ...arr[rowIdx], [campo]: valor }
      return { ...pg, [tipo]: arr }
    }))
  }

  const atualizarObservacoes = (pgIdx, valor) => {
    setCotPyParcPaginas(prev => prev.map((pg, i) => i === pgIdx ? { ...pg, observacoes: valor } : pg))
  }

  const formatarTituloParcelamento = pg => {
    const seguradora = String(pg?.seguradora || '').trim() || 'Seguradora'
    const numero = String(pg?.numero || '').trim()
    return numero ? `${seguradora} Nº ${numero}` : seguradora
  }

  const gerarCotacaoPDFPython = async () => {
    if (!cotPyCompacto || !cotPyParc) return
    setGerandoCotPy(true)
    try {
      const fd = new FormData()
      fd.append('compacto', cotPyCompacto)
      const parcFileToSend = cotPyParcPaginas.length > 0
        ? gerarExcelParcelamentoEmMemoria(cotPyParcPaginas)
        : cotPyParc
      fd.append('parcelamento', parcFileToSend)
      if (cotPyCobs.length > 0) fd.append('coberturas_json', JSON.stringify(cotPyCobs))
      if (cotPyNomes.length > 0) fd.append('nomes_json', JSON.stringify(cotPyNomes))
      if (Object.keys(cotPyDadosGerais).length > 0) fd.append('dados_gerais_json', JSON.stringify(cotPyDadosGerais))
      if (cotPyFranquia.length > 0) fd.append('franquia_json', JSON.stringify(cotPyFranquia))
      const mesclarUrl = process.env.REACT_APP_MESCLAR_PDF_URL || 'http://localhost:5000'
      const res = await fetch(`${mesclarUrl}/cotacao-pdf`, { method: 'POST', body: fd })
      if (!res.ok) {
        const msg = await res.text()
        throw new Error(msg)
      }
      const blob = await res.blob()

      if (cotPyOrigemNegociacao) {
        const dg = cotPyDadosGerais
        const nomeArquivo = await nomearAnexoCotacao(editandoId, dg.segurado || clienteSelecionado?.nome)

        if (editandoId) {
          // Negociação já existe (gerando uma cotação extra durante a edição) — anexa direto,
          // sem mexer no cliente/ramo já definidos no card.
          try {
            const dadosUrl = await blobParaDataUrl(blob)
            await api.post(`/funil/${editandoId}/anexos`, { nome: nomeArquivo, tamanho: blob.size, dados: dadosUrl })
            setFunilAnexosRefreshKey(k => k + 1)
          } catch (err) {
            avisar('Erro ao anexar a Cotação PDF: ' + (err.response?.data?.erro || err.message))
          }
        } else {
          // Negociação nova, ainda não salva — casa cliente pelo CPF/nome (mesma lógica do
          // antigo importarPDF) e guarda o PDF pra anexar automaticamente quando o card for
          // criado (ainda não existe id pra anexar agora).
          let clienteEncontrado = null
          if (dg.cpf) {
            clienteEncontrado = clientes.find(c => c.cpf_cnpj?.replace(/\D/g, '') === dg.cpf.replace(/\D/g, ''))
          }
          if (!clienteEncontrado && dg.segurado) {
            clienteEncontrado = clientes.find(c => c.nome?.toLowerCase().includes(dg.segurado.toLowerCase().split(' ')[0]))
          }
          const nascMatch = String(dg.nascimento || '').match(/(\d{2})\/(\d{2})\/(\d{4})/)
          const dataNascimentoIso = nascMatch ? `${nascMatch[3]}-${nascMatch[2]}-${nascMatch[1]}` : ''

          setForm(f => ({
            ...f,
            ...(clienteEncontrado ? { cliente_id: String(clienteEncontrado.id) } : {}),
            ramo: 'Automóvel',
          }))
          setInfoPDF({
            segurado: dg.segurado || '—',
            cpf: dg.cpf || '—',
            cep: dg.cep || '',
            tipo: 'Novo',
            ramo: 'Automóvel',
            sexo: dg.sexo || '',
            estado_civil: dg.estado_civil || '',
            data_nascimento: dataNascimentoIso,
            clienteEncontrado: clienteEncontrado?.nome || null,
            dadosParaCriar: !clienteEncontrado ? { segurado: dg.segurado, cpf: dg.cpf, cep: dg.cep, sexo: dg.sexo, estado_civil: dg.estado_civil, data_nascimento: dataNascimentoIso } : null,
          })
          setCotacaoPdfPendente({ blob, nome: nomeArquivo })
        }
      }

      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'cotacao.pdf'
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      fecharModalCotPy()
    } catch (err) {
      avisar('Erro ao gerar PDF: ' + err.message)
    } finally {
      setGerandoCotPy(false)
    }
  }

  const clienteSelecionado = clientes.find(c => c.id === Number(form.cliente_id))

  // Sugestoes da busca de cliente (Nova Negociação) — por nome ou CPF, mesmo padrao da busca
  // geral de Propostas.jsx.
  const sugestoesClientes = (() => {
    const q = buscaCliente.toLowerCase().trim()
    if (!q) return []
    const qRaw = q.replace(/\D/g, '')
    return clientes
      .filter(c => c.nome?.toLowerCase().includes(q) || (qRaw && c.cpf_cnpj?.replace(/\D/g, '').includes(qRaw)))
      .slice(0, 8)
  })()
  const navBuscaCliente = useNavegacaoLista(sugestoesClientes.length, (i) => {
    const c = sugestoesClientes[i]
    setForm(f => ({ ...f, cliente_id: String(c.id) }))
    setBuscaCliente(c.nome)
    setMostrarSugestoesCliente(false)
  })

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#888' }}>
      Carregando CRM...
    </div>
  )

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ background: 'transparent', padding: '32px 24px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>CRM</h2>
          <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>Acompanhe o funil de vendas, do primeiro contato ao fechamento.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input ref={inputExcelRef}  type="file" accept=".pdf"        style={{ display: 'none' }} onChange={e => { setOrcaCompactoFile(e.target.files[0] || null); e.target.value = '' }} />
          <input ref={inputParcRef}      type="file" accept=".pdf" style={{ display: 'none' }} onChange={exportarParcelamentoExcel} />
          <input ref={inputParcPDFRef}   type="file" accept=".pdf" style={{ display: 'none' }} onChange={exportarParcelamentoPDF} />
          <input ref={inputCotPDFRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={exportarCotacaoExcelParaPDF} />
          <button
            className="btn btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, border: '1px solid var(--primary)' }}
            onClick={() => setModalCotPy(true)}
          >
            PDF Cotação Avulsa ↗
          </button>
          <button className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }} onClick={() => abrirNova()}>
            + NOVA NEGOCIAÇÃO
          </button>
          <button
            className="btn btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, border: '1px solid var(--primary)' }}
            onClick={() => navigate('/renovacoes')}
          >
            Renovações
          </button>
          <button
            className="btn btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, border: '1px solid #B22222' }}
            onClick={() => navigate('/perdidos')}
          >
            Perdidos
          </button>
        </div>
      </div>

      <div style={{ padding: 24 }}>
        {/* Filtros */}
        <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#1A1A2E', display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
            <span>☰</span> Central de Negócios
          </div>
          <input className="form-input" placeholder="Buscar por nome, CPF/CNPJ, telefone ou observação..." value={busca} onChange={e => setBusca(e.target.value)}
            style={{ maxWidth: 380, width: '100%', border: '2px solid rgba(var(--primary-rgb),0.15)', borderRadius: 10 }} />
          <select className="form-select" style={{ width: 'auto', minWidth: 150, border: '2px solid rgba(var(--primary-rgb),0.15)', borderRadius: 10 }} value={filtroRamo} onChange={e => setFiltroRamo(e.target.value)}>
            <option value="todos">Todos os ramos</option>
            {RAMOS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <select className="form-select" style={{ width: 'auto', minWidth: 170, border: '2px solid rgba(var(--primary-rgb),0.15)', borderRadius: 10 }} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
            <option value="todos">Todos os tipos</option>
            {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {/* Kanban Board */}
        {/* Altura fixa na tela: cada coluna rola na vertical e a barra horizontal fica sempre visivel */}
        <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 16, alignItems: 'flex-start', height: 'calc(100vh - 150px)', minHeight: 420 }}>
          {COLUNAS.map(col => {
            const colCards = porColuna(col.id)
            return (
              <div key={col.id} style={{ minWidth: 260, width: 260, flexShrink: 0, maxHeight: '100%', display: 'flex', flexDirection: 'column' }} onDragOver={onDragOver} onDrop={e => onDrop(e, col.id)}>
                {/* Header da coluna */}
                <div style={{ background: col.cor, borderRadius: '8px 8px 0 0', padding: '10px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{col.label}</span>
                    <span style={{ background: 'rgba(255,255,255,0.25)', color: '#fff', borderRadius: 20, padding: '1px 8px', fontSize: 12, fontWeight: 700 }}>{colCards.length}</span>
                  </div>
                </div>

                {/* Cards */}
                <div style={{ background: '#EAECF0', borderRadius: '0 0 8px 8px', padding: 8, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 60, overflowY: 'auto' }}>
                  {colCards.map(card => (
                    <div
                      key={card.id}
                      draggable
                      onDragStart={e => onDragStart(e, card)}
                      onClick={() => abrirEditar(card)}
                      style={{
                        background: '#fff', borderRadius: 8, padding: '12px 14px',
                        border: '1px solid #E0E0E0', cursor: 'grab',
                        boxShadow: dragging?.id === card.id ? '0 4px 12px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.06)',
                        opacity: dragging?.id === card.id ? 0.5 : 1,
                        transition: 'box-shadow 0.15s',
                      }}
                    >
                      {/* Nome */}
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#1A1A2E', marginBottom: 6 }}>{card.nome}</div>

                      {/* Telefone */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8a19.79 19.79 0 01-3.07-8.63A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                        </svg>
                        <span style={{ fontSize: 12, color: '#666' }}>{(() => { try { const t = typeof card.telefones === 'string' ? JSON.parse(card.telefones) : card.telefones; return t?.[0]?.numero || '—' } catch { return '—' } })()}</span>
                      </div>

                      {/* Ramo | Tipo */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, background: '#EEF2FF', color: '#3730A3', borderRadius: 4, padding: '2px 7px' }}>{card.ramo || '—'}</span>
                        <span style={{ fontSize: 11, color: '#999' }}>|</span>
                        <span style={{ fontSize: 11, color: '#666' }}>{card.tipo || '—'}</span>
                      </div>
                    </div>
                  ))}

                  {/* Botão adicionar */}
                  <button
                    onClick={() => abrirNova(col.id)}
                    style={{ background: 'transparent', border: '1.5px dashed #C0C0C0', borderRadius: 8, color: '#999', fontSize: 12, padding: '8px', width: '100%', cursor: 'pointer', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#888'; e.currentTarget.style.color = '#555' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#C0C0C0'; e.currentTarget.style.color = '#999' }}
                  >
                    + Adicionar negociação
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Modal */}
      {modalAberto && (
        <div className="modal-overlay" onClick={fecharModal}>
          <div className="modal" style={{ maxWidth: 920 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={editandoId ? { marginBottom: 4 } : undefined}>
              {!editandoId && <span className="modal-title">Nova Negociação</span>}
              <button className="modal-close" onClick={fecharModal} style={editandoId ? { marginLeft: 'auto' } : undefined}>×</button>
            </div>

            {/* Editar: nome do cliente vira o "titulo" do card, com link pra pagina dele.
                Nova Negociação: no mesmo lugar entra importar PDF + buscar/incluir cliente. */}
            {editandoId && clienteSelecionado && (
              <button
                type="button"
                onClick={irParaCliente}
                title="Abrir cadastro do cliente"
                style={{
                  display: 'block', background: 'none', border: 'none', padding: 0, marginBottom: 6,
                  cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                  fontSize: 21, fontWeight: 700, color: 'var(--primary)'
                }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
              >
                {clienteSelecionado.nome}
              </button>
            )}

            {!editandoId && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, border: '1.5px dashed var(--primary)' }}
                    onClick={() => { setCotPyOrigemNegociacao(true); setModalCotPy(true) }}
                  >
                    Gerar Cotação PDF
                  </button>

                  {cotacaoPdfPendente && (
                    <div style={{ marginTop: 10, background: '#EAF3DE', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#3B6D11', fontWeight: 700 }}>
                      📄 {cotacaoPdfPendente.nome} pronto — será anexado ao salvar a negociação.
                    </div>
                  )}

                  {infoPDF && (
                    <div style={{ marginTop: 10, background: infoPDF.clienteEncontrado ? '#EAF3DE' : '#FFF8E6', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: infoPDF.clienteEncontrado ? '#3B6D11' : '#854F0B' }}>
                        {infoPDF.clienteEncontrado ? `✅ Cliente encontrado: ${infoPDF.clienteEncontrado}` : '⚠️ Cliente não encontrado no sistema'}
                      </div>
                      <div style={{ color: '#555', display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: infoPDF.dadosParaCriar ? 10 : 0 }}>
                        <span><strong>Segurado:</strong> {infoPDF.segurado}</span>
                        <span><strong>CPF:</strong> {infoPDF.cpf}</span>
                        <span><strong>Tipo:</strong> {infoPDF.tipo}</span>
                        <span><strong>Ramo:</strong> {infoPDF.ramo}</span>
                      </div>
                      {infoPDF.dadosParaCriar && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          style={{ marginTop: 6, width: '100%' }}
                          onClick={criarClientePDF}
                          disabled={salvando}
                        >
                          {salvando ? 'Criando...' : '+ Criar cliente automaticamente com esses dados'}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Cliente *</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <input
                        className="form-input"
                        placeholder="Buscar por nome ou CPF..."
                        value={form.cliente_id ? (clienteSelecionado?.nome || '') : buscaCliente}
                        onChange={e => {
                          setForm(f => ({ ...f, cliente_id: '' }))
                          setBuscaCliente(e.target.value)
                          setMostrarSugestoesCliente(true)
                          navBuscaCliente.reset()
                        }}
                        onFocus={() => setMostrarSugestoesCliente(true)}
                        onBlur={() => setTimeout(() => setMostrarSugestoesCliente(false), 150)}
                        onKeyDown={navBuscaCliente.onKeyDown}
                      />
                      {mostrarSugestoesCliente && buscaCliente.trim() && sugestoesClientes.length > 0 && (
                        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #D9DDE3', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto' }}>
                          {sugestoesClientes.map((c, i) => (
                            <button
                              key={c.id}
                              type="button"
                              onMouseDown={() => {
                                setForm(f => ({ ...f, cliente_id: String(c.id) }))
                                setBuscaCliente(c.nome)
                                setMostrarSugestoesCliente(false)
                              }}
                              onMouseEnter={() => navBuscaCliente.setIndice(i)}
                              style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: i === navBuscaCliente.indice ? 'rgba(var(--primary-rgb),0.08)' : '#fff', padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F3F5', fontFamily: 'inherit' }}
                            >
                              <div style={{ fontSize: 13, color: '#1A1A2E' }}>{c.nome}</div>
                              <div style={{ fontSize: 11, color: '#888' }}>{c.cpf_cnpj || '—'}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Botão novo cliente */}
                    <button
                      type="button"
                      title="Adicionar novo cliente"
                      className="btn btn-secondary"
                      style={{ flexShrink: 0, padding: '0 12px', fontSize: 18 }}
                      onClick={() => { sessionStorage.setItem('novoCliente', '1'); navigate('/clientes') }}
                    >+</button>
                    {/* Botão editar cliente */}
                    {form.cliente_id && (
                      <button
                        type="button"
                        title="Editar dados do cliente"
                        className="btn btn-secondary btn-icon"
                        onClick={irParaCliente}
                      ><IconEditar size={14} /></button>
                    )}
                  </div>
                </div>

                {/* CPF/Telefone do cliente encontrado/selecionado */}
                {clienteSelecionado && (
                  <div style={{ background: '#F8F9FA', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#555', display: 'flex', gap: 16 }}>
                    <span><strong>CPF/CNPJ:</strong> {clienteSelecionado.cpf_cnpj || '—'}</span>
                    <span><strong>Tel:</strong> {clienteSelecionado.telefones?.[0]?.numero || '—'}</span>
                  </div>
                )}
              </>
            )}

            {/* Dados (esquerda) e Histórico de Atendimento (direita), lado a lado, mesma largura —
                antes eram 2 abas; o Registrar Atendimento mora só do lado do Histórico agora (era
                duplicado nas 2 abas antes). */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
              <div ref={colunaEsquerdaRef} style={{ flex: '1 1 320px', minWidth: 0 }}>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Ramo</label>
                    <select className="form-select" value={form.ramo} onChange={e => setForm(f => ({ ...f, ramo: e.target.value }))}>
                      {RAMOS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tipo</label>
                    <select className="form-select" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
                      {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                {form.apolice_origem_id && apoliceOrigem && !TIPOS_SEM_APOLICE_VINCULADA.includes(form.tipo) && (
                  <div style={{ border: '1px solid #eee', borderRadius: 10, padding: 14, marginBottom: 12, background: '#fafafa' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
                      Apólice Vinculada
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13, marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 11, color: '#888' }}>Nº da Apólice</div>
                        <div style={{ fontWeight: 600 }}>{apoliceOrigem.numero_apolice || apoliceOrigem.emissao_numero_apolice || '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#888' }}>Seguradora</div>
                        <div style={{ fontWeight: 600 }}>{apoliceOrigem.seguradora || '—'}</div>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 13, marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 11, color: '#888' }}>Prêmio Líquido</div>
                        <div style={{ fontWeight: 600 }}>{somaPremioOrigem('liquido') ? formatMoney(somaPremioOrigem('liquido')) : '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#888' }}>% de Comissão</div>
                        <div style={{ fontWeight: 600 }}>{apoliceOrigem.comissao_pct || '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#888' }}>Prêmio Total</div>
                        <div style={{ fontWeight: 600 }}>{somaPremioOrigem('total') ? formatMoney(somaPremioOrigem('total')) : '—'}</div>
                      </div>
                    </div>
                    {cadeiaOrigem.length > 1 && (
                      <div style={{ fontSize: 11, color: '#888', marginTop: -6, marginBottom: 12 }}>
                        Prêmios somando a apólice + {cadeiaOrigem.length - 1} endosso{cadeiaOrigem.length > 2 ? 's' : ''}.
                      </div>
                    )}
                    {(() => {
                      const lista = v => { try { const x = typeof v === 'string' ? JSON.parse(v) : v; return Array.isArray(x) ? x : [] } catch { return [] } }
                      const tels = lista(clienteSelecionado?.telefones).filter(t => t?.numero)
                      const mails = lista(clienteSelecionado?.emails).filter(Boolean)
                      if (!tels.length && !mails.length) return null
                      return (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13, marginBottom: 12 }}>
                          <div>
                            <div style={{ fontSize: 11, color: '#888' }}>Telefones</div>
                            {tels.length ? tels.map((t, i) => (
                              <div key={i} style={{ fontWeight: 600 }}>{t.numero}{t.nome && t.nome !== 'Telefone' && <span style={{ fontWeight: 400, color: '#888' }}> ({t.nome})</span>}</div>
                            )) : <div style={{ fontWeight: 600 }}>—</div>}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 11, color: '#888' }}>E-mails</div>
                            {mails.length ? mails.map((m, i) => (
                              <div key={i} style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{m}</div>
                            )) : <div style={{ fontWeight: 600 }}>—</div>}
                          </div>
                        </div>
                      )
                    })()}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={irParaApolice}>Ver Apólice</button>
                      <div ref={pdfMenuOrigemRef} style={{ position: 'relative' }}>
                        <button type="button" className="btn btn-secondary btn-sm"
                          onClick={() => {
                            const cadeia = cadeiaOrigem.length ? cadeiaOrigem : [apoliceOrigem]
                            if (cadeia.length === 1) { verPdfDocumento(cadeia[0]); return }
                            setPdfMenuOrigem(v => !v)
                          }}>
                          {cadeiaOrigem.length > 1 ? 'Ver PDF ▾' : 'Ver PDF'}
                        </button>
                        {pdfMenuOrigem && (
                          <div style={{
                            position: 'absolute', bottom: '100%', left: 0, marginBottom: 4, zIndex: 50,
                            background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.12)', minWidth: 160, overflow: 'hidden'
                          }}>
                            {cadeiaOrigem.map((d, i) => (
                              <button key={d.id} type="button" onClick={() => { setPdfMenuOrigem(false); verPdfDocumento(d) }} style={{
                                display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                                background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap', fontFamily: 'inherit'
                              }} onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                                {i === 0 ? 'PDF da Apólice' : `PDF do Endosso ${d.emissao_numero_endosso || i}`}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Etapa</label>
                  <select className="form-select" value={form.etapa} onChange={e => setForm(f => ({ ...f, etapa: e.target.value }))}>
                    {COLUNAS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>

                {editandoId && (
                  <div style={{ marginBottom: 16 }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, border: '1.5px dashed var(--primary)' }}
                      onClick={() => { setCotPyOrigemNegociacao(true); setModalCotPy(true) }}
                    >
                      Gerar Cotação PDF
                    </button>
                  </div>
                )}

                {editandoId ? <FunilAnexos funilId={editandoId} refreshSignal={funilAnexosRefreshKey} /> : (
                  <div className="form-group">
                    <label className="form-label">Anexos</label>
                    <div style={{ padding: 14, textAlign: 'center', color: '#aaa', background: '#f8f9fa', borderRadius: 8, fontSize: 12 }}>
                      Disponível depois de criar a negociação.
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Observações</label>
                  <textarea className="form-input" rows={3} placeholder="Observações..." value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))} style={{ resize: 'vertical' }} />
                </div>
              </div>

              <div style={{
                flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column',
                ...(alturaColunaEsquerda ? { maxHeight: alturaColunaEsquerda, overflow: 'hidden' } : {})
              }}>
                <div className="form-label" style={{ fontSize: 16, fontWeight: 600, color: '#2F2F2E', marginBottom: 10 }}>Histórico de Atendimento</div>
                {editandoId ? <FunilHistorico funilId={editandoId} /> : (
                  <div style={{ padding: 24, textAlign: 'center', color: '#aaa', background: '#f8f9fa', borderRadius: 8, fontSize: 13 }}>
                    Você poderá registrar atendimentos depois de criar a negociação.
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              {editandoId && (
                <button className="btn btn-danger btn-icon" title="Excluir" onClick={excluir}><IconExcluir size={16} /></button>
              )}
              <button className="btn btn-secondary" onClick={fecharModal}>Cancelar</button>
              <button className="btn btn-primary" onClick={() => salvar()} disabled={salvando}>
                {salvando ? 'Salvando...' : editandoId ? 'Salvar alterações' : 'Criar negociação'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Orça Compacto */}
      {modalOrcaCompacto && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1A1A2E', marginBottom: 18 }}>📊 Orça Compacto → Excel</div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>PDF Compacto <span style={{ color: '#e53935' }}>*</span></label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => inputExcelRef.current.click()}>Selecionar PDF</button>
                <span style={{ fontSize: 12, color: orcaCompactoFile ? 'var(--primary)' : '#aaa' }}>{orcaCompactoFile ? orcaCompactoFile.name : 'Nenhum arquivo'}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setModalOrcaCompacto(false); setOrcaCompactoFile(null) }}>Cancelar</button>
              <button className="btn btn-primary" disabled={!orcaCompactoFile} onClick={() => exportarCotacaoExcel(orcaCompactoFile)}>Gerar Excel</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cotação PDF Python */}
      {modalCotPy && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

          {/* Etapa 0: selecionar PDFs originais (fluxo automático) */}
          {cotPyStep === 'pdfs' && (
            <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#1A1A2E', marginBottom: 4 }}>Gerar Cotação PDF</div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>Selecione os PDFs originais — os Excels são gerados automaticamente</div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>PDF Orça Compacto</label>
                <input type="file" accept=".pdf" onChange={e => setCotPyPdfCompacto(e.target.files[0] || null)} style={{ width: '100%', fontSize: 13 }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>PDF Parcelamento</label>
                <input type="file" accept=".pdf" onChange={e => setCotPyPdfParc(e.target.files[0] || null)} style={{ width: '100%', fontSize: 13 }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <button style={{ background: 'none', border: 'none', color: '#999', fontSize: 12, cursor: 'pointer', padding: 0, textDecoration: 'underline' }} onClick={() => setCotPyStep('files')}>
                  Já tenho os Excels gerados →
                </button>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={fecharModalCotPy}>Cancelar</button>
                <button className="btn btn-primary" onClick={gerarFluxoDePDFs} disabled={!cotPyPdfCompacto || !cotPyPdfParc || processandoPdfs}>
                  {processandoPdfs ? '⏳ Processando...' : 'Gerar →'}
                </button>
              </div>
            </div>
          )}

          {/* Etapa 1: selecionar arquivos Excel manualmente */}
          {cotPyStep === 'files' && (
            <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#1A1A2E', marginBottom: 20 }}>Gerar Cotação PDF</div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>Excel Compacto</label>
                <input ref={cotPyCompactoRef} type="file" accept=".xlsx,.xls" onChange={e => setCotPyCompacto(e.target.files[0] || null)} style={{ width: '100%', fontSize: 13 }} />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 6 }}>Excel Parcelamento</label>
                <input ref={cotPyParcRef} type="file" accept=".xlsx,.xls" onChange={e => setCotPyParc(e.target.files[0] || null)} style={{ width: '100%', fontSize: 13 }} />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={fecharModalCotPy}>Cancelar</button>
                <button className="btn btn-primary" onClick={avancarParaConfirmar} disabled={!cotPyCompacto || !cotPyParc}>Próximo →</button>
              </div>
            </div>
          )}

          {/* Etapa 2: confirmar edição de coberturas */}
          {cotPyStep === 'confirmar' && (
            <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#1A1A2E', marginBottom: 12 }}>Coberturas</div>
              <div style={{ fontSize: 14, color: '#444', marginBottom: 24 }}>Deseja revisar ou alterar as coberturas antes de gerar o PDF?</div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={fecharModalCotPy}>Cancelar</button>
                <button className="btn btn-secondary" onClick={gerarCotacaoPDFPython} disabled={gerandoCotPy}>
                  {gerandoCotPy ? '⏳ Gerando...' : 'Não, gerar direto'}
                </button>
                <button className="btn btn-primary" onClick={() => setCotPyStep('coberturas')}>Sim, editar</button>
              </div>
            </div>
          )}

          {/* Etapa 3: editar coberturas */}
          {cotPyStep === 'coberturas' && (
            <div style={{ background: '#F5F6F8', borderRadius: 12, width: '96vw', maxWidth: 1360, maxHeight: '94vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
              <div style={{ padding: '28px 32px 4px' }}>
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1A1A2E' }}>Gerador de Cotação PDF</h2>
                <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>Revise os dados antes de gerar o PDF final.</div>
              </div>

              <div style={{ padding: '16px 32px 32px', display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Dados Gerais */}
                <div className="card">
                  <div className="card-title" style={{ color: 'var(--primary)' }}>Dados Gerais</div>
                  <div className="form-grid-3">
                    <div>
                      <div className="form-group">
                        <label className="form-label">Segurado</label>
                        <input className="form-input" value={cotPyDadosGerais.segurado || ''} onChange={e => atualizarDadoGeral('segurado', e.target.value)} />
                      </div>
                      <div className="form-grid">
                        <div className="form-group">
                          <label className="form-label">Tipo de Seguro</label>
                          <input className="form-input" value={cotPyDadosGerais.tipo_seguro || ''} onChange={e => atualizarDadoGeral('tipo_seguro', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">CEP</label>
                          <input className="form-input" value={cotPyDadosGerais.cep || ''} onChange={e => atualizarDadoGeral('cep', e.target.value)} />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Condutor</label>
                        <input className="form-input" value={cotPyDadosGerais.condutor || ''} onChange={e => atualizarDadoGeral('condutor', e.target.value)} />
                      </div>
                      <div className="form-grid">
                        <div className="form-group">
                          <label className="form-label">Nascimento</label>
                          <input className="form-input" value={cotPyDadosGerais.nascimento || ''} onChange={e => atualizarDadoGeral('nascimento', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Sexo</label>
                          <input className="form-input" value={cotPyDadosGerais.sexo || ''} onChange={e => atualizarDadoGeral('sexo', e.target.value)} />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Condutor jovem residente entre 18 e 25 anos?</label>
                        <input className="form-input" value={cotPyDadosGerais.jovem_cond || ''} onChange={e => atualizarDadoGeral('jovem_cond', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <div className="form-group">
                        <label className="form-label">CPF/CNPJ</label>
                        <input className="form-input" value={cotPyDadosGerais.cpf || ''} onChange={e => atualizarDadoGeral('cpf', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Vigência</label>
                        <input className="form-input" value={cotPyDadosGerais.vigencia || ''} onChange={e => atualizarDadoGeral('vigencia', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">CPF</label>
                        <input className="form-input" value={cotPyDadosGerais.cpf_cond || ''} onChange={e => atualizarDadoGeral('cpf_cond', e.target.value)} />
                      </div>
                      <div className="form-grid">
                        <div className="form-group">
                          <label className="form-label">Est. Civil</label>
                          <input className="form-input" value={cotPyDadosGerais.estado_civil || ''} onChange={e => atualizarDadoGeral('estado_civil', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Uso</label>
                          <input className="form-input" value={cotPyDadosGerais.uso || ''} onChange={e => atualizarDadoGeral('uso', e.target.value)} />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">CEP Pernoite</label>
                        <input className="form-input" value={cotPyDadosGerais.cep_pernoite || ''} onChange={e => atualizarDadoGeral('cep_pernoite', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <div className="form-group">
                        <label className="form-label">Veículo</label>
                        <input className="form-input" value={cotPyDadosGerais.veiculo || ''} onChange={e => atualizarDadoGeral('veiculo', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Ano / Modelo</label>
                        <input className="form-input" value={cotPyDadosGerais.ano_modelo || ''} onChange={e => atualizarDadoGeral('ano_modelo', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Placa</label>
                        <input className="form-input" value={cotPyDadosGerais.placa || ''} onChange={e => atualizarDadoGeral('placa', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Chassi</label>
                        <input className="form-input" value={cotPyDadosGerais.chassi || ''} onChange={e => atualizarDadoGeral('chassi', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">FIPE</label>
                        <input className="form-input" value={cotPyDadosGerais.fipe || ''} onChange={e => atualizarDadoGeral('fipe', e.target.value)} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Coberturas + Franquia */}
                <div className="card">
                  <div className="card-title" style={{ color: 'var(--primary)' }}>Coberturas</div>
                  <div className="table-wrap">
                    <table style={{ minWidth: LARGURA_COL_LABEL + cotPyNomes.length * LARGURA_COL_SEG, tableLayout: 'fixed' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', width: LARGURA_COL_LABEL }}>Cobertura</th>
                          {cotPyNomes.map((n, i) => (
                            <th key={i} style={{ color: 'var(--primary)', textAlign: 'center', width: LARGURA_COL_SEG, fontSize: 13 }}>{n}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {cotPyCobs.map((row, ri) => {
                          const opcoes = opcoesParaLabel(row.label)
                          return (
                            <tr key={ri}>
                              <td style={{ fontWeight: 500 }}>{row.label}</td>
                              {cotPyNomes.map((_, ci) => (
                                <td key={ci}>
                                  {opcoes ? (
                                    <InputComOpcoes value={row.values[ci]} onChange={v => atualizarCobertura(ri, ci, v)} opcoes={opcoes} />
                                  ) : (
                                    <input
                                      className="form-input"
                                      value={row.values[ci] || ''}
                                      onChange={e => atualizarCobertura(ri, ci, e.target.value)}
                                      style={{ fontSize: 12, padding: '6px 8px' }}
                                    />
                                  )}
                                </td>
                              ))}
                            </tr>
                          )
                        })}
                        {cotPyFranquia.length > 0 && (
                          <>
                            <tr>
                              <td style={{ fontWeight: 500 }}>Franquia - Tipo</td>
                              {cotPyFranquia.map((f, ci) => (
                                <td key={ci}>
                                  <InputComOpcoes value={f.tipo} onChange={v => atualizarFranquia(ci, 'tipo', v)} opcoes={OPCOES_COBERTURA.franquia} />
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td style={{ fontWeight: 500 }}>Franquia - Veículo</td>
                              {cotPyFranquia.map((f, ci) => (
                                <td key={ci}>
                                  <input
                                    className="form-input"
                                    value={f.valor || ''}
                                    onChange={e => atualizarFranquia(ci, 'valor', e.target.value)}
                                    style={{ fontSize: 12, padding: '6px 8px' }}
                                  />
                                </td>
                              ))}
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Parcelamentos — colunas = seguradoras (igual Coberturas); dentro de cada
                    coluna as formas de pagamento (Crédito/Débito/Boleto) ficam empilhadas uma
                    embaixo da outra, em vez de lado a lado (pedido do usuário em 12/09). */}
                {cotPyParcPaginas.length > 0 && (
                  <div className="card">
                    <div className="card-title" style={{ color: 'var(--primary)' }}>Parcelamentos</div>
                    <div className="table-wrap">
                      <table style={{ minWidth: LARGURA_COL_LABEL + cotPyParcPaginas.length * LARGURA_COL_SEG, tableLayout: 'fixed' }}>
                        <thead>
                          <tr>
                            <th style={{ width: LARGURA_COL_LABEL, padding: 0 }}></th>
                            {cotPyParcPaginas.map((pg, pgIdx) => (
                              <th key={pgIdx} style={{ color: 'var(--primary)', textAlign: 'center', width: LARGURA_COL_SEG, fontSize: 13 }}>{formatarTituloParcelamento(pg)}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { chave: 'credito', label: 'CRÉDITO' },
                            { chave: 'debito',  label: 'DÉBITO' },
                            { chave: 'boleto',  label: 'BOLETO' },
                          ].map(({ chave, label }) => {
                            const maxLinhas = Math.max(...cotPyParcPaginas.map(pg => pg[chave].length))
                            if (maxLinhas === 0) return null
                            return (
                              <Fragment key={chave}>
                                <tr>
                                  <td></td>
                                  {cotPyParcPaginas.map((pg, pgIdx) => (
                                    <td key={pgIdx} style={{ textAlign: 'center' }}>
                                      {pg[chave].length > 0 && <span className="badge badge-blue">{label}</span>}
                                    </td>
                                  ))}
                                </tr>
                                {Array.from({ length: maxLinhas }).map((_, ri) => (
                                  <tr key={ri}>
                                    <td style={{ width: 0, padding: 0 }}></td>
                                    {cotPyParcPaginas.map((pg, pgIdx) => {
                                      const item = pg[chave][ri]
                                      if (!item) return <td key={pgIdx}></td>
                                      return (
                                        <td key={pgIdx}>
                                          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                            <input className="form-input" value={item.parcelamento} onChange={e => atualizarParcelamento(pgIdx, chave, ri, 'parcelamento', e.target.value)} style={{ width: 132, fontSize: 11, padding: '5px 6px' }} />
                                            <input className="form-input" value={item.total} onChange={e => atualizarParcelamento(pgIdx, chave, ri, 'total', e.target.value)} placeholder="Total" style={{ width: 96, fontSize: 11, padding: '5px 6px' }} />
                                          </div>
                                        </td>
                                      )
                                    })}
                                  </tr>
                                ))}
                              </Fragment>
                            )
                          })}
                          <tr>
                            <td style={{ fontWeight: 500 }}>Observações</td>
                            {cotPyParcPaginas.map((pg, pgIdx) => (
                              <td key={pgIdx}>
                                <input
                                  className="form-input"
                                  value={pg.observacoes || ''}
                                  onChange={e => atualizarObservacoes(pgIdx, e.target.value)}
                                  placeholder="Observações"
                                  style={{ fontSize: 11, padding: '5px 7px' }}
                                />
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={fecharModalCotPy} disabled={gerandoCotPy}>Cancelar</button>
                  <button className="btn btn-primary" onClick={gerarCotacaoPDFPython} disabled={gerandoCotPy}>
                    {gerandoCotPy ? '⏳ Gerando...' : 'Gerar PDF'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      )}

      {confirmarExcluirCard && (
        <div className="modal-overlay" style={{ zIndex: 300 }} onClick={() => setConfirmarExcluirCard(false)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700 }}>Excluir negociação?</h2>
            <p style={{ fontSize: 13, color: '#555', margin: '0 0 20px' }}>Essa ação não pode ser desfeita.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmarExcluirCard(false)}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={confirmarExclusaoCard}>Excluir</button>
            </div>
          </div>
        </div>
      )}

      {perdaPendente && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: 'min(460px, 100%)', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 14, padding: 26, boxShadow: '0 18px 48px rgba(0,0,0,0.24)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 19, color: '#B22222' }}>Negociação perdida</h3>
            <div style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>{perdaPendente.card?.nome}</div>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Por que a negociação foi perdida?</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
              {MOTIVOS_PERDA.map(m => (
                <label key={m} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type="radio" name="motivo-perda" checked={perda.motivo === m} onChange={() => setPerda(p => ({ ...p, motivo: m }))} style={{ accentColor: 'var(--primary)' }} />
                  {m}
                </label>
              ))}
            </div>
            <textarea className="form-input" rows={2} placeholder={perda.motivo === 'Outro' ? 'Descreva o motivo (obrigatório)' : 'Observação (opcional)'}
              value={perda.obs} onChange={e => setPerda(p => ({ ...p, obs: e.target.value }))} style={{ width: '100%', resize: 'vertical', marginBottom: 16 }} />
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Deseja retornar contato no próximo ano?</div>
            <div style={{ display: 'flex', gap: 20, marginBottom: 6 }}>
              {[['sim', 'Sim'], ['nao', 'Não']].map(([v, l]) => (
                <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type="radio" name="retornar-contato" checked={perda.retornar === v} onChange={() => setPerda(p => ({ ...p, retornar: v }))} style={{ accentColor: 'var(--primary)' }} />
                  {l}
                </label>
              ))}
            </div>
            {perda.retornar === 'sim' && (
              <div className="form-group" style={{ marginBottom: 6, marginTop: 6, maxWidth: 220 }}>
                <label className="form-label">Data do retorno</label>
                <input className="form-input" type="date" min={hojeISO()} value={perda.dataRetorno}
                  onChange={e => setPerda(p => ({ ...p, dataRetorno: e.target.value }))} />
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Fica como "Data do Retorno" na tela Perdidos.</div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <button className="btn btn-secondary" disabled={registrandoPerda} onClick={() => setPerdaPendente(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={confirmarPerda}
                disabled={registrandoPerda || !perda.motivo || !perda.retornar || (perda.retornar === 'sim' && !perda.dataRetorno) || (perda.motivo === 'Outro' && !perda.obs.trim())}>
                {registrandoPerda ? 'Salvando...' : 'Mover para Perdido'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmarCadastroProposta && !formaCadastroProposta && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 380, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>📄</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1a1a2e' }}>Cadastrar a proposta agora?</div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 24 }}>
              Negociação fechada com <strong>{confirmarCadastroProposta.nome}</strong>. Deseja cadastrar a proposta agora?
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={lembrarDepois}>Agora não</button>
              <button className="btn btn-primary" onClick={() => setFormaCadastroProposta(true)}>Sim, cadastrar</button>
            </div>
          </div>
        </div>
      )}

      {confirmarCadastroProposta && formaCadastroProposta && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: 'min(480px, 100%)', background: '#fff', borderRadius: 14, padding: 26, boxShadow: '0 18px 48px rgba(0,0,0,0.24)' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 19 }}>Como deseja cadastrar?</h3>
            <div style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
              Proposta de <strong>{confirmarCadastroProposta.nome}</strong>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <button type="button" onClick={cadastrarPropostaViaPDF} style={{
                padding: 18, border: '1px solid var(--primary)', borderRadius: 10, background: '#FFF7F3', color: '#9A2A00',
                cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit'
              }}>
                <strong style={{ display: 'block', marginBottom: 6 }}>Importar PDF da proposta</strong>
                <span style={{ fontSize: 12, lineHeight: 1.45 }}>Lê o PDF e preenche os dados automaticamente.</span>
              </button>
              <button type="button" onClick={cadastrarPropostaManualmente} style={{
                padding: 18, border: '1px solid #D9DDE3', borderRadius: 10, background: '#fff', color: '#333',
                cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit'
              }}>
                <strong style={{ display: 'block', marginBottom: 6 }}>Cadastrar manualmente</strong>
                <span style={{ fontSize: 12, lineHeight: 1.45 }}>Abre o formulário de Proposta em branco, com o cliente já selecionado.</span>
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setFormaCadastroProposta(false)}>Voltar</button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
